import React, { useState, useEffect, Suspense, lazy } from 'react';
import { FileText, Play, Pause } from 'lucide-react';

// OS Components - Chargement immédiat pour les composants critiques
import { SystemHeader } from './components/os/SystemHeader';
import { Window } from './components/os/Window';
import { HelpWindow } from './components/os/HelpWindow';
import { TerminalLogs } from './components/os/TerminalLogs';
import { SystemBlueprint } from './components/os/SystemBlueprint';
import { CertificationWallpaper } from './components/os/CertificationWallpaper';
import { AlbionIcons } from './components/os/AlbionIcons';

// Composants de notification - Chargement immédiat car légers
import { NotificationContainer } from './components/os/NotificationContainer';
import { KernelCrashAlert } from './components/os/KernelCrashAlert';
import { SaveDialog } from './components/os/SaveDialog';

// Hooks et utilitaires
import { useSystemStress } from './hooks/useSystemStress';
import { useTutorial } from './hooks/useTutorial';
import { useFileProgression } from './hooks/useFileProgression';
import { useMessageNotifications } from './hooks/useMessageNotifications';
import { useLanguage } from './hooks/useLanguage';
import { FILE_CONTENTS } from './data/fileSystem';
import { quitApplication } from './utils/electron';

// Styles
import './styles/systemStress.css';

// Lazy loading pour les composants lourds
const BoardPanel = lazy(() => import('./components/BoardPanel').then(module => ({ default: module.BoardPanel })));
const FileExplorer = lazy(() => import('./components/os/FileExplorer').then(module => ({ default: module.FileExplorer })));
const ImageEnhancer = lazy(() => import('./components/os/ImageEnhancer').then(module => ({ default: module.ImageEnhancer })));
const FileViewer = lazy(() => import('./components/os/FileViewer').then(module => ({ default: module.default })));
const Messenger = lazy(() => import('./components/os/Messenger').then(module => ({ default: module.Messenger })));
const TheEar = lazy(() => import('./components/os/TheEar').then(module => ({ default: module.TheEar })));
const Properties = lazy(() => import('./components/os/Properties').then(module => ({ default: module.Properties })));
const WallpaperSelector = lazy(() => import('./components/os/WallpaperSelector').then(module => ({ default: module.WallpaperSelector })));
const ProcessMonitor = lazy(() => import('./components/os/ProcessMonitor').then(module => ({ default: module.ProcessMonitor })));
const GameSettings = lazy(() => import('./components/os/GameSettings').then(module => ({ default: module.GameSettings })));
const Snake = lazy(() => import('./components/os/Snake').then(module => ({ default: module.Snake })));
const SpaceInvaders = lazy(() => import('./components/os/SpaceInvaders').then(module => ({ default: module.SpaceInvaders })));
const AlbionPlayer = lazy(() => import('./components/os/AlbionPlayer').then(module => ({ default: module.AlbionPlayer })));
const StationProfile = lazy(() => import('./components/os/StationProfile').then(module => ({ default: module.StationProfile })));

/**
 * Composant de fallback pour le chargement des composants lazy
 */
const LoadingFallback: React.FC<{ componentName?: string }> = ({ componentName = 'composant' }) => (
  <div className="flex items-center justify-center h-full bg-slate-900 text-slate-400">
    <div className="text-center">
      <div className="animate-spin w-8 h-8 border-2 border-cyan-400 border-t-transparent rounded-full mx-auto mb-2"></div>
      <div className="text-sm font-mono">CHARGEMENT {componentName.toUpperCase()}...</div>
    </div>
  </div>
);

/**
 * Composant Widget Horloge pour le bureau
 */
const DesktopClock: React.FC<{ isVisible: boolean }> = ({ isVisible }) => {
  const [time, setTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => {
      setTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const formatTime = (date: Date) => {
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const seconds = date.getSeconds().toString().padStart(2, '0');
    return { hours, minutes, seconds };
  };

  const formatDate = (date: Date) => {
    const days = ['DIM', 'LUN', 'MAR', 'MER', 'JEU', 'VEN', 'SAM'];
    const months = ['JAN', 'FÉV', 'MAR', 'AVR', 'MAI', 'JUN', 'JUL', 'AOÛ', 'SEP', 'OCT', 'NOV', 'DÉC'];
    
    const dayName = days[date.getDay()];
    const day = date.getDate().toString().padStart(2, '0');
    const month = months[date.getMonth()];
    const year = date.getFullYear();
    
    return `${dayName} ${day} ${month} ${year}`;
  };

  const { hours, minutes, seconds } = formatTime(time);

  return (
    <div 
      className="fixed top-16 right-4 z-50 select-none pointer-events-none transition-opacity duration-1000"
      style={{ opacity: isVisible ? 1 : 0 }}
    >
      <div className="relative bg-slate-900/95 backdrop-blur-sm border border-cyan-500/30 rounded-lg shadow-xl p-4 w-[240px]">
        {/* Effet de scan line */}
        <div className="absolute inset-0 overflow-hidden rounded-lg pointer-events-none">
          <div className="absolute inset-0 bg-gradient-to-b from-cyan-500/3 via-transparent to-cyan-500/3 animate-pulse"></div>
          <div 
            className="absolute inset-0 bg-gradient-to-b from-transparent via-cyan-400/5 to-transparent"
            style={{
              animation: 'scan 3s linear infinite',
              height: '100%'
            }}
          ></div>
        </div>
        
        {/* Label */}
        <div className="text-center text-[10px] text-cyan-400/40 uppercase tracking-[0.3em] mb-2 font-bold">
          System Time
        </div>
        
        {/* Heure */}
        <div className="relative flex items-center justify-center gap-1">
          {/* Heures */}
          <div className="relative">
            <div className="text-5xl font-bold text-cyan-400/90 font-mono tracking-wider relative z-10" 
                 style={{ textShadow: '0 0 10px rgba(34,211,238,0.3)' }}>
              {hours}
            </div>
          </div>
          
          {/* Séparateur animé */}
          <div className="text-5xl font-bold text-cyan-400/90 animate-pulse" 
               style={{ textShadow: '0 0 8px rgba(34,211,238,0.2)' }}>
            :
          </div>
          
          {/* Minutes */}
          <div className="relative">
            <div className="text-5xl font-bold text-cyan-400/90 font-mono tracking-wider relative z-10" 
                 style={{ textShadow: '0 0 10px rgba(34,211,238,0.3)' }}>
              {minutes}
            </div>
          </div>
          
          {/* Secondes */}
          <div className="ml-2 flex flex-col items-center justify-center">
            <div className="text-xs text-cyan-400/30 uppercase tracking-wider font-bold mb-0.5">sec</div>
            <div className="text-2xl font-bold text-cyan-400/70 font-mono" 
                 style={{ textShadow: '0 0 6px rgba(34,211,238,0.2)' }}>
              {seconds}
            </div>
          </div>
        </div>
        
        {/* Barre de progression des secondes */}
        <div className="mt-3 h-1 bg-slate-800 rounded-full overflow-hidden">
          <div 
            className="h-full bg-gradient-to-r from-cyan-500/60 to-cyan-300/60 rounded-full transition-all duration-1000 ease-linear"
            style={{ 
              width: `${(parseInt(seconds) / 60) * 100}%`,
              boxShadow: '0 0 6px rgba(34,211,238,0.3)'
            }}
          ></div>
        </div>
        
        {/* Coins décoratifs */}
        <div className="absolute top-0 left-0 w-3 h-3 border-t border-l border-cyan-400/30"></div>
        <div className="absolute top-0 right-0 w-3 h-3 border-t border-r border-cyan-400/30"></div>
        <div className="absolute bottom-0 left-0 w-3 h-3 border-b border-l border-cyan-400/30"></div>
        <div className="absolute bottom-0 right-0 w-3 h-3 border-b border-r border-cyan-400/30"></div>
      </div>
      
      <style>{`
        @keyframes scan {
          0% {
            transform: translateY(-100%);
          }
          100% {
            transform: translateY(100%);
          }
        }
      `}</style>
    </div>
  );
};

/**
 * Composant Widget Notes pour le bureau
 */
const DesktopNotes: React.FC<{ isVisible: boolean }> = ({ isVisible }) => {
  const [notesList, setNotesList] = useState<Array<{ id: number; content: string; timestamp: number }>>(() => {
    const saved = localStorage.getItem('ALBION_DESKTOP_NOTES');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch {
        return [{ id: 1, content: '', timestamp: Date.now() }];
      }
    }
    return [{ id: 1, content: '', timestamp: Date.now() }];
  });

  const [currentIndex, setCurrentIndex] = useState(0);
  const [currentContent, setCurrentContent] = useState(notesList[0]?.content || '');

  const saveNotes = (notes: typeof notesList) => {
    localStorage.setItem('ALBION_DESKTOP_NOTES', JSON.stringify(notes));
    setNotesList(notes);
  };

  const handleSave = () => {
    const updatedNotes = [...notesList];
    updatedNotes[currentIndex] = {
      ...updatedNotes[currentIndex],
      content: currentContent,
      timestamp: Date.now()
    };
    saveNotes(updatedNotes);
  };

  const handleNewNote = () => {
    const newNote = {
      id: Math.max(...notesList.map(n => n.id), 0) + 1,
      content: '',
      timestamp: Date.now()
    };
    const updatedNotes = [...notesList, newNote];
    saveNotes(updatedNotes);
    setCurrentIndex(updatedNotes.length - 1);
    setCurrentContent('');
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      const newIndex = currentIndex - 1;
      setCurrentIndex(newIndex);
      setCurrentContent(notesList[newIndex].content);
    }
  };

  const handleNext = () => {
    if (currentIndex < notesList.length - 1) {
      const newIndex = currentIndex + 1;
      setCurrentIndex(newIndex);
      setCurrentContent(notesList[newIndex].content);
    }
  };

  const handleDelete = () => {
    if (notesList.length === 1) {
      // Ne pas supprimer la dernière note, juste la vider
      const updatedNotes = [{ id: 1, content: '', timestamp: Date.now() }];
      saveNotes(updatedNotes);
      setCurrentIndex(0);
      setCurrentContent('');
    } else {
      const updatedNotes = notesList.filter((_, i) => i !== currentIndex);
      saveNotes(updatedNotes);
      const newIndex = Math.min(currentIndex, updatedNotes.length - 1);
      setCurrentIndex(newIndex);
      setCurrentContent(updatedNotes[newIndex].content);
    }
  };

  return (
    <div 
      className="fixed top-[220px] right-4 z-50 select-none pointer-events-auto transition-opacity duration-1000"
      style={{ opacity: isVisible ? 1 : 0 }}
    >
      <div className="relative bg-slate-900/95 backdrop-blur-sm border border-cyan-500/30 rounded-lg shadow-xl p-4 w-[240px]">
        {/* Label */}
        <div className="text-center text-[10px] text-cyan-400/40 uppercase tracking-[0.3em] mb-2 font-bold">
          Quick Notes
        </div>
        
        {/* Zone de texte */}
        <textarea
          value={currentContent}
          onChange={(e) => setCurrentContent(e.target.value)}
          placeholder="Tapez vos notes ici..."
          className="w-full h-32 bg-slate-800/50 border border-slate-700 rounded p-2 text-slate-300 text-sm font-mono resize-none focus:outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/30 placeholder-slate-600"
          style={{ scrollbarWidth: 'thin' }}
        />
        
        {/* Contrôles */}
        <div className="mt-2 flex items-center justify-between gap-2">
          {/* Navigation */}
          <div className="flex items-center gap-1">
            <button
              onClick={handlePrevious}
              disabled={currentIndex === 0}
              className="w-6 h-6 flex items-center justify-center bg-slate-800 border border-slate-700 rounded text-cyan-400 hover:bg-slate-700 hover:border-cyan-500/50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors text-xs"
              title="Note précédente"
            >
              ←
            </button>
            <span className="text-[10px] text-slate-400 font-mono min-w-[45px] text-center">
              {currentIndex + 1}/{notesList.length}
            </span>
            <button
              onClick={handleNext}
              disabled={currentIndex === notesList.length - 1}
              className="w-6 h-6 flex items-center justify-center bg-slate-800 border border-slate-700 rounded text-cyan-400 hover:bg-slate-700 hover:border-cyan-500/50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors text-xs"
              title="Note suivante"
            >
              →
            </button>
          </div>
          
          {/* Actions */}
          <div className="flex items-center gap-1">
            <button
              onClick={handleNewNote}
              className="px-2 py-1 bg-slate-800 border border-slate-700 rounded text-cyan-400 hover:bg-slate-700 hover:border-cyan-500/50 transition-colors text-[10px] font-bold"
              title="Nouvelle note"
            >
              +
            </button>
            <button
              onClick={handleDelete}
              className="px-2 py-1 bg-slate-800 border border-slate-700 rounded text-red-400 hover:bg-slate-700 hover:border-red-500/50 transition-colors text-[10px] font-bold"
              title="Supprimer"
            >
              ×
            </button>
            <button
              onClick={handleSave}
              className="px-2 py-1 bg-cyan-500/20 border border-cyan-500/50 rounded text-cyan-400 hover:bg-cyan-500/30 hover:border-cyan-400 transition-colors text-[10px] font-bold uppercase"
              title="Enregistrer"
            >
              Save
            </button>
          </div>
        </div>
        
        {/* Coins décoratifs */}
        <div className="absolute top-0 left-0 w-3 h-3 border-t border-l border-cyan-400/30"></div>
        <div className="absolute top-0 right-0 w-3 h-3 border-t border-r border-cyan-400/30"></div>
        <div className="absolute bottom-0 left-0 w-3 h-3 border-b border-l border-cyan-400/30"></div>
        <div className="absolute bottom-0 right-0 w-3 h-3 border-b border-r border-cyan-400/30"></div>
      </div>
    </div>
  );
};

/**
 * Composant Widget Terminal Logs pour le bureau
 */
const TerminalLogsWidget: React.FC<{ isVisible: boolean; onClose: () => void }> = ({ isVisible, onClose }) => {
  return (
    <div 
      className="fixed bottom-[60px] right-4 z-50 select-none pointer-events-auto transition-opacity duration-1000" 
      style={{ opacity: isVisible ? 1 : 0 }}
    >
      <div className="relative bg-slate-900/95 backdrop-blur-sm border border-cyan-500/30 rounded-lg shadow-xl w-[500px] h-[300px] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-cyan-500/20 px-3 py-2 flex-shrink-0">
          <div className="text-[10px] text-cyan-400/60 uppercase tracking-[0.3em] font-bold">
            System Logs
          </div>
          <button
            onClick={onClose}
            className="w-5 h-5 flex items-center justify-center bg-slate-800 border border-slate-700 rounded text-red-400 hover:bg-red-500/20 hover:border-red-500/50 transition-colors text-xs font-bold"
            title="Fermer"
          >
            ×
          </button>
        </div>
        
        {/* Terminal Content */}
        <div className="flex-1 overflow-hidden">
          <TerminalLogs />
        </div>
        
        {/* Coins décoratifs */}
        <div className="absolute top-0 left-0 w-3 h-3 border-t border-l border-cyan-400/30"></div>
        <div className="absolute top-0 right-0 w-3 h-3 border-t border-r border-cyan-400/30"></div>
        <div className="absolute bottom-0 left-0 w-3 h-3 border-b border-l border-cyan-400/30"></div>
        <div className="absolute bottom-0 right-0 w-3 h-3 border-b border-r border-cyan-400/30"></div>
      </div>
    </div>
  );
};

/**
 * Composant Helper pour les icônes déplaçables du bureau
 */
const DraggableDesktopIcon: React.FC<{
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
  unreadCount?: number;
  position: { x: number; y: number };
  onMove: (x: number, y: number) => void;
  size: 'small' | 'medium' | 'large';
  isSelected: boolean;
  onSelect: (label: string) => void;
}> = ({ label, icon, onClick, unreadCount = 0, position, onMove, size, isSelected, onSelect }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState<{ x: number; y: number } | null>(null);
  const [hasMoved, setHasMoved] = useState(false);
  const [lastClickTime, setLastClickTime] = useState(0);
  const [isVisible, setIsVisible] = useState(true); // Toujours visible pour éviter les problèmes de positionnement

  // Animation d'apparition désactivée pour les icônes (problème de positionnement)
  // React.useEffect(() => {
  //   const delay = Math.random() * 500 + 200;
  //   const timer = setTimeout(() => {
  //     setIsVisible(true);
  //   }, delay);
  //   return () => clearTimeout(timer);
  // }, []);

  const iconSize = size === 'small' ? 'w-16' : size === 'large' ? 'w-32' : 'w-24';
  const labelSize = size === 'small' ? 'text-[8px]' : size === 'large' ? 'text-[11px]' : 'text-[9px]';

  const handleMouseDown = (e: React.MouseEvent) => {
    if (e.button === 0) { // Clic gauche seulement
      setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y });
      setIsDragging(true);
      setHasMoved(false);
      
      // Émettre un événement pour simuler la charge CPU
      window.dispatchEvent(new CustomEvent('ICON_DRAG_START'));
      
      e.preventDefault();
    }
  };

  const handleMouseMove = React.useCallback((e: MouseEvent) => {
    if (isDragging && dragStart) {
      const newX = Math.max(0, Math.min(window.innerWidth - 100, e.clientX - dragStart.x));
      const newY = Math.max(40, Math.min(window.innerHeight - 120, e.clientY - dragStart.y));

      // Détecter si l'icône a bougé de manière significative
      const deltaX = Math.abs(newX - position.x);
      const deltaY = Math.abs(newY - position.y);
      if (deltaX > 5 || deltaY > 5) {
        setHasMoved(true);
      }

      onMove(newX, newY);
    }
  }, [isDragging, dragStart, onMove, position.x, position.y]);

  const handleMouseUp = React.useCallback(() => {
    setIsDragging(false);
    setDragStart(null);
  }, []);

  React.useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();

    // Si l'icône a bougé, ne pas déclencher le clic
    if (hasMoved) {
      setHasMoved(false);
      return;
    }

    const currentTime = Date.now();
    const timeDiff = currentTime - lastClickTime;

    if (timeDiff < 500 && lastClickTime > 0) {
      // Double-clic détecté
      onClick();
      setTimeout(() => {
        onSelect(''); // Désélectionner après le double-clic
      }, 150);
      setLastClickTime(0); // Reset pour éviter les triple-clics
    } else {
      // Premier clic - juste sélectionner
      onSelect(label);
      setLastClickTime(currentTime);
    }
  };

  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const menuWidth = 180;
    const menuHeight = 120;
    const x = e.clientX + menuWidth > window.innerWidth ? e.clientX - menuWidth : e.clientX;
    const y = e.clientY + menuHeight > window.innerHeight ? e.clientY - menuHeight : e.clientY;

    // Fermer les autres menus et ouvrir celui de l'icône
    window.dispatchEvent(new CustomEvent('ICON_CONTEXT_MENU', {
      detail: { iconName: label, x, y }
    }));
  };

  return (
    <div
      className={`${iconSize} flex flex-col items-center gap-2 cursor-pointer group select-none pointer-events-auto absolute transition-all duration-800 ease-out
        ${isDragging ? 'z-[150] opacity-80' : 'z-[60]'}
        ${isVisible ? 'opacity-100' : 'opacity-0'}
      `}
      style={{
        left: position.x,
        top: position.y,
        animationDelay: `${Math.random() * 500}ms`
      }}
      onMouseDown={handleMouseDown}
      onClick={handleClick}
      onContextMenu={handleContextMenu}
    >
      <div className="w-10 h-10 flex items-center justify-center mb-1 transition-transform group-hover:scale-110 drop-shadow-[0_0_8px_rgba(0,0,0,0.5)] relative">
        {icon}
        {/* Badge de notification */}
        {unreadCount > 0 && (
          <div className="absolute -top-1 -right-1 w-5 h-5 bg-red-600 text-white text-[10px] font-bold rounded-full flex items-center justify-center animate-pulse border border-white shadow-lg">
            {unreadCount > 9 ? '9+' : unreadCount}
          </div>
        )}
      </div>

      {/* Label style high-tech gouvernemental */}
      <div className={`
        ${labelSize} text-center font-mono tracking-wider px-2 py-1 rounded transition-all duration-150 break-all line-clamp-2 min-h-[18px] flex items-center justify-center border
        ${isSelected
          ? 'bg-cyan-600 text-slate-900 border-cyan-400 shadow-lg shadow-cyan-500/50'
          : 'text-slate-300 bg-slate-800/60 border-slate-600/50 group-hover:bg-slate-700/80 group-hover:text-cyan-400 group-hover:border-cyan-500/50 group-hover:shadow-md group-hover:shadow-cyan-500/20'
        }
      `}>
        {label}
      </div>
    </div>
  );
};

/**
 * Composant principal UchroniaOS - Simulateur d'OS années 90 pour le jeu Albion.
 * Gère l'interface complète du système d'exploitation avec fenêtres, applications et interactions.
 * 
 * @returns {JSX.Element} Interface complète du système d'exploitation
 */
const UchroniaOS: React.FC = () => {
  const { t } = useLanguage();
  const mainContainerRef = React.useRef<HTMLDivElement>(null);

  // Effet radical pour bloquer tout scroll du navigateur qui pourrait être déclenché
  // quand une fenêtre sort de l'écran ou prend le focus.
  React.useEffect(() => {
    const preventScroll = () => {
      if (mainContainerRef.current) {
        mainContainerRef.current.scrollTop = 0;
        mainContainerRef.current.scrollLeft = 0;
      }
      window.scrollTo(0, 0);
    };

    window.addEventListener('scroll', preventScroll, { passive: false });
    document.addEventListener('scroll', preventScroll, { passive: false });

    return () => {
      window.removeEventListener('scroll', preventScroll);
      document.removeEventListener('scroll', preventScroll);
    };
  }, []);
  const { step, advanceStep } = useTutorial();
  const { notifications, dismissNotification, advanceStep: advanceFileStep } = useFileProgression();
  const {
    notifications: messageNotifications,
    dismissNotification: dismissMessageNotification,
    getUnreadCount,
    setMessengerOpen
  } = useMessageNotifications();

  // État des paramètres du jeu
  const [gameSettings, setGameSettings] = useState(() => {
    const saved = localStorage.getItem('ALBION_GAME_SETTINGS');
    if (saved) {
      try {
        const parsedSettings = JSON.parse(saved);
        // S'assurer que fullscreenMode est défini
        return {
          soundEnabled: true,
          soundVolume: 0.7,
          crtEffects: true,
          animationSpeed: 1,
          autoSave: false,
          debugMode: false,
          fullscreenMode: true,
          ...parsedSettings // Écraser avec les valeurs sauvegardées
        };
      } catch (e) { }
    }
    return {
      soundEnabled: true,
      soundVolume: 0.7,
      crtEffects: true,
      animationSpeed: 1,
      autoSave: false,
      debugMode: false,
      fullscreenMode: true // Ajouter le mode plein écran par défaut
    };
  });

  // Écouter les changements de paramètres (sans gestion du plein écran)
  React.useEffect(() => {
    const handleSettingsChange = (e: CustomEvent) => {
      const newSettings = e.detail;
      console.log('UchroniaOS - Settings changed, updating local state');
      setGameSettings(newSettings);
    };

    window.addEventListener('GAME_SETTINGS_CHANGED', handleSettingsChange as EventListener);
    return () => window.removeEventListener('GAME_SETTINGS_CHANGED', handleSettingsChange as EventListener);
  }, []);

  // Wallpaper State - Gestion des fonds d'écran prédéfinis uniquement
  const [wallpaperType, setWallpaperType] = useState<'default' | 'certification'>(() => {
    const saved = localStorage.getItem('wallpaperType');
    return (saved === 'certification' ? 'certification' : 'default');
  });
  const [wallpaperTransitioning, setWallpaperTransitioning] = useState(false);
  const [nextWallpaperType, setNextWallpaperType] = useState<'default' | 'certification' | null>(null);
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number } | null>(null);
  const [desktopContextMenu, setDesktopContextMenu] = useState<{ x: number; y: number } | null>(null);
  const [iconSize, setIconSize] = useState<'small' | 'medium' | 'large'>(() => {
    const saved = localStorage.getItem('ALBION_ICON_SIZE');
    return saved ? JSON.parse(saved) : 'medium';
  });
  const [desktopIcons, setDesktopIcons] = useState<Record<string, { x: number; y: number }>>(() => {
    const saved = localStorage.getItem('ALBION_DESKTOP_ICONS');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) { }
    }
    // Positions par défaut des icônes (alignées comme l'auto-arrangement)
    // Espacement: 100px vertical, 120px horizontal, départ à (40, 40)
    return {
      'TABLEAU': { x: 40, y: 40 },      // Colonne 1, ligne 1
      'FICHIERS': { x: 40, y: 140 },    // Colonne 1, ligne 2
      'MESSAGER': { x: 40, y: 240 },    // Colonne 1, ligne 3
      'L\'OREILLE': { x: 40, y: 340 },  // Colonne 1, ligne 4
      'MA_STATION': { x: 40, y: 440 },  // Colonne 1, ligne 5
      'CORBEILLE': { x: 40, y: 540 }    // Colonne 1, ligne 6
    };
  });

  const [desktopShortcuts, setDesktopShortcuts] = useState<Record<string, any>>(() => {
    const saved = localStorage.getItem('ALBION_DESKTOP_SHORTCUTS');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) { }
    }
    return {};
  });


  // Sauvegarde des positions d'icônes
  React.useEffect(() => {
    localStorage.setItem('ALBION_DESKTOP_ICONS', JSON.stringify(desktopIcons));
    localStorage.setItem('ALBION_ICON_SIZE', JSON.stringify(iconSize));
    localStorage.setItem('ALBION_DESKTOP_SHORTCUTS', JSON.stringify(desktopShortcuts));
  }, [desktopIcons, iconSize, desktopShortcuts]);

  // Gestionnaire de fenêtres
  const [openFiles, setOpenFiles] = useState<{ id: string; fileId: string }[]>([]);
  const [openWindows, setOpenWindows] = useState<string[]>([]);
  const [minimizedWindows, setMinimizedWindows] = useState<string[]>([]);
  const [activeWindow, setActiveWindow] = useState<string | null>(null);
  const [windowStack, setWindowStack] = useState<string[]>([]); // Ordre de pile pour le z-index
  const [isGlitching, setIsGlitching] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [propertiesIcon, setPropertiesIcon] = useState<string | null>(null);
  const [selectedIcon, setSelectedIcon] = useState<string | null>(null);
  const [isProcessMonitorOpen, setIsProcessMonitorOpen] = useState(false);
  const [isSaveDialogOpen, setIsSaveDialogOpen] = useState(false);
  const [isNotificationPanelOpen, setIsNotificationPanelOpen] = useState(false); // État pour le panneau de notifications
  const [unviewedNotificationCount, setUnviewedNotificationCount] = useState(0); // Compteur de notifications non vues
  const [desktopInitialized, setDesktopInitialized] = useState(false);
  const [desktopFadingOut, setDesktopFadingOut] = useState(false); // État pour le fondu de fermeture
  const [windowsAppearing, setWindowsAppearing] = useState<Set<string>>(new Set());
  const [isLogsWidgetVisible, setIsLogsWidgetVisible] = useState(true); // Widget LOGS_SYS visible par défaut
  const [isShuttingDownSequence, setIsShuttingDownSequence] = useState(false); // Séquence de fermeture en cours
  const [widgetsVisible, setWidgetsVisible] = useState(false); // Contrôle la visibilité des widgets (horloge, notes, logs) - démarre caché pour l'animation

  // Fonction utilitaire pour ouvrir une fenêtre avec animation
  const openWindowWithAnimation = (windowId: string) => {
    // Marquer la fenêtre comme en cours d'apparition
    setWindowsAppearing(prev => new Set([...prev, windowId]));

    // Ajouter la fenêtre aux fenêtres ouvertes
    setOpenWindows(prev => {
      if (!prev.includes(windowId)) {
        // Émettre l'événement d'ouverture de fenêtre pour le tutoriel
        window.dispatchEvent(new CustomEvent('WINDOW_OPENED', {
          detail: { windowId }
        }));
        return [...prev, windowId];
      }
      return prev;
    });

    // Ajouter la fenêtre en haut de la pile
    setWindowStack(prev => {
      const newStack = prev.filter(w => w !== windowId);
      return [...newStack, windowId]; // Ajouter à la fin (plus haut z-index)
    });

    // Retirer le marqueur d'apparition après l'animation
    setTimeout(() => {
      setWindowsAppearing(prev => {
        const newSet = new Set(prev);
        newSet.delete(windowId);
        return newSet;
      });
    }, 600); // Durée de l'animation d'apparition

    // Activer la fenêtre et la déminimiser si nécessaire
    setActiveWindow(windowId);
    setMinimizedWindows(prev => prev.filter(w => w !== windowId));

    // Log système pour l'immersion
    window.dispatchEvent(new CustomEvent('SCANNER_LOG', {
      detail: { message: t('systemLogs.windowInitialized', { windowId: windowId.toUpperCase() }) }
    }));
  };

  // Calcul de la charge CPU et mémoire (synchronisé entre SystemHeader et ProcessMonitor)
  const [cpuLoad, setCpuLoad] = useState(15);
  const [memoryUsage, setMemoryUsage] = useState(50);
  const [cpuSpike, setCpuSpike] = useState(0); // Pic temporaire de CPU pour les actions utilisateur

  // Système de stress basé sur la charge CPU
  const { stressLevel, freezeActive, inversionActive, getStressClasses, getInputStyle } = useSystemStress(cpuLoad);

  // Affichage du niveau de stress dans la console (développement)
  React.useEffect(() => {
    if (stressLevel.level !== 'NORMAL') {
      console.log(`🔥 NIVEAU DE STRESS: ${stressLevel.level} (CPU: ${stressLevel.cpuLoad}%)`, stressLevel.effects);
    }
  }, [stressLevel.level, stressLevel.cpuLoad]);

  // Fonction pour ajouter un pic temporaire de CPU lors d'actions utilisateur
  const addCpuSpike = React.useCallback((amount: number, duration: number = 500) => {
    setCpuSpike(prev => Math.min(prev + amount, 30)); // Max +30% de spike
    setTimeout(() => {
      setCpuSpike(prev => Math.max(prev - amount, 0));
    }, duration);
  }, []);

  // Écouter les événements d'actions utilisateur pour simuler la charge CPU
  React.useEffect(() => {
    const handleIconDrag = () => addCpuSpike(5, 300); // +5% pendant 300ms
    const handleWindowResize = () => addCpuSpike(8, 400); // +8% pendant 400ms
    const handleFileDrop = () => addCpuSpike(10, 600); // +10% pendant 600ms
    const handleWindowOpen = () => addCpuSpike(12, 800); // +12% pendant 800ms

    window.addEventListener('ICON_DRAG_START', handleIconDrag);
    window.addEventListener('WINDOW_RESIZE_START', handleWindowResize);
    window.addEventListener('FILE_DROP', handleFileDrop);
    window.addEventListener('WINDOW_OPENED', handleWindowOpen);

    return () => {
      window.removeEventListener('ICON_DRAG_START', handleIconDrag);
      window.removeEventListener('WINDOW_RESIZE_START', handleWindowResize);
      window.removeEventListener('FILE_DROP', handleFileDrop);
      window.removeEventListener('WINDOW_OPENED', handleWindowOpen);
    };
  }, [addCpuSpike]);

  React.useEffect(() => {
    const baseLoad = 15; // Charge système de base
    const windowLoad = openWindows.length * 8; // 8% par fenêtre ouverte
    const processLoad = (notifications.length + messageNotifications.length) * 5; // 5% par processus actif
    const randomVariation = Math.floor(Math.random() * 6 - 3); // Variation ±3%

    const totalLoad = Math.min(Math.max(baseLoad + windowLoad + processLoad + cpuSpike + randomVariation, 5), 99);
    setCpuLoad(totalLoad);

    // Calcul de la mémoire basé sur les mêmes facteurs
    const baseMem = 35; // Mémoire système de base
    const windowMem = openWindows.length * 12; // 12MB par fenêtre ouverte
    const processMem = (notifications.length + messageNotifications.length) * 3; // 3MB par processus actif
    const memVariation = Math.floor(Math.random() * 8 - 4); // Variation ±4MB

    const totalMem = Math.min(Math.max(baseMem + windowMem + processMem + memVariation, 30), 180);
    setMemoryUsage(totalMem);
  }, [openWindows.length, notifications.length, messageNotifications.length, cpuSpike]);

  // Fonction de test pour simuler la surchauffe (développement uniquement)
  const simulateStress = (level: 'NORMAL' | 'ALERTE' | 'CRITIQUE' | 'RUPTURE') => {
    switch (level) {
      case 'ALERTE':
        setCpuLoad(75);
        break;
      case 'CRITIQUE':
        setCpuLoad(90);
        break;
      case 'RUPTURE':
        setCpuLoad(99);
        break;
      default:
        setCpuLoad(15);
    }
  };

  // Raccourcis clavier pour tester les niveaux de stress (développement)
  // Ctrl+Shift+1 : NORMAL (15% CPU)
  // Ctrl+Shift+2 : ALERTE (75% CPU) - Curseur fantôme, icônes qui tremblent, bruit de ventilateur
  // Ctrl+Shift+3 : CRITIQUE (90% CPU) - Délai input, distorsion fenêtres, alerte kernel clignotante
  // Ctrl+Shift+4 : RUPTURE (99% CPU) - Décalage chromatique, inversion texte, gel système, caractères ASCII volants
  React.useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.shiftKey) {
        switch (e.key) {
          case '1':
            simulateStress('NORMAL');
            console.log('Stress simulé: NORMAL');
            break;
          case '2':
            simulateStress('ALERTE');
            console.log('Stress simulé: ALERTE');
            break;
          case '3':
            simulateStress('CRITIQUE');
            console.log('Stress simulé: CRITIQUE');
            break;
          case '4':
            simulateStress('RUPTURE');
            console.log('Stress simulé: RUPTURE');
            break;
        }
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, []);
  // Persistence Logic - Load avec délai après connexion
  React.useEffect(() => {
    // Initialiser avec un bureau vide au démarrage (aucune fenêtre)
    setOpenWindows([]);
    setActiveWindow(null);

    // Fonction pour restaurer les fenêtres
    const restoreWindows = () => {
      console.log('UchroniaOS - Début de la restauration des fenêtres...');

      // Log système pour l'immersion
      window.dispatchEvent(new CustomEvent('SCANNER_LOG', {
        detail: { message: t('systemLogs.sessionEstablished') }
      }));

      // Attendre 3 secondes avant de restaurer les fenêtres sauvegardées
      setTimeout(() => {
        const saved = localStorage.getItem('ALBION_SAVE_DATA');
        console.log('UchroniaOS - Données sauvegardées trouvées:', saved);

        if (saved) {
          try {
            const data = JSON.parse(saved);
            console.log('UchroniaOS - Données parsées:', data);

            if (data.windows && data.windows.open) {
              console.log('UchroniaOS - Fenêtres à restaurer:', data.windows.open);

              // Restaurer exactement les fenêtres qui étaient ouvertes lors de la fermeture
              window.dispatchEvent(new CustomEvent('SCANNER_LOG', {
                detail: { message: t('systemLogs.restoringWindows', { count: data.windows.open.length }) }
              }));

              // Restaurer les données des fenêtres immédiatement
              setMinimizedWindows(data.windows.minimized || []);
              setOpenFiles(data.windows.files || []);

              // Ouvrir les fenêtres une par une avec animation
              const windowsToOpen = data.windows.open || [];
              windowsToOpen.forEach((windowId: string, index: number) => {
                setTimeout(() => {
                  console.log(`UchroniaOS - Ouverture de la fenêtre ${windowId} (${index + 1}/${windowsToOpen.length})`);

                  // Utiliser la fonction d'ouverture animée
                  openWindowWithAnimation(windowId);

                  // Définir la fenêtre active à la dernière fenêtre ouverte
                  if (index === windowsToOpen.length - 1) {
                    setTimeout(() => {
                      setActiveWindow(data.windows.active || windowId);
                    }, 100);
                  }
                }, index * 400); // Délai de 400ms entre chaque fenêtre
              });

              console.log('UchroniaOS - Séquence de restauration lancée');

              // Déclencher le message de bienvenue de la démo après la restauration
              const totalRestoreTime = windowsToOpen.length * 400 + 5000; // Temps de restauration + 5 secondes
              setTimeout(() => {
                const demoStarted = localStorage.getItem('ALBION_DEMO_TIMER_STARTED');
                if (!demoStarted) {
                  console.log('UchroniaOS - Déclenchement de DEMO_START');
                  localStorage.setItem('ALBION_DEMO_TIMER_STARTED', 'true');
                  window.dispatchEvent(new CustomEvent('DEMO_START'));
                }
              }, totalRestoreTime);
            } else {
              console.log('UchroniaOS - Aucune fenêtre sauvegardée à restaurer');
              window.dispatchEvent(new CustomEvent('SCANNER_LOG', {
                detail: { message: t('systemLogs.cleanEnvironment') }
              }));
              // Pas de fenêtre par défaut, le widget LOGS_SYS est toujours visible
              setOpenWindows([]);
              setActiveWindow(null);

              // Déclencher le message de bienvenue de la démo après 5 secondes
              setTimeout(() => {
                const demoStarted = localStorage.getItem('ALBION_DEMO_TIMER_STARTED');
                if (!demoStarted) {
                  console.log('UchroniaOS - Déclenchement de DEMO_START (première session)');
                  localStorage.setItem('ALBION_DEMO_TIMER_STARTED', 'true');
                  window.dispatchEvent(new CustomEvent('DEMO_START'));
                }
              }, 5000);
            }
          } catch (e) {
            console.error("Erreur de chargement de la sauvegarde", e);
            window.dispatchEvent(new CustomEvent('SCANNER_LOG', {
              detail: { message: t('systemLogs.restorationError') }
            }));
            // En cas d'erreur, pas de fenêtre par défaut
            setOpenWindows([]);
            setActiveWindow(null);
          }
        } else {
          console.log('UchroniaOS - Première connexion, pas de données à restaurer');
          window.dispatchEvent(new CustomEvent('SCANNER_LOG', {
            detail: { message: t('systemLogs.firstSession') }
          }));
          // Pas de fenêtre par défaut, le widget LOGS_SYS est toujours visible
          setOpenWindows([]);
          setActiveWindow(null);

          // Déclencher le message de bienvenue de la démo après 5 secondes
          setTimeout(() => {
            const demoStarted = localStorage.getItem('ALBION_DEMO_TIMER_STARTED');
            if (!demoStarted) {
              console.log('UchroniaOS - Déclenchement de DEMO_START (première connexion)');
              localStorage.setItem('ALBION_DEMO_TIMER_STARTED', 'true');
              window.dispatchEvent(new CustomEvent('DEMO_START'));
            }
          }, 5000);
        }
      }, 3000); // Délai de 3 secondes
    };

    // Écouter l'événement de connexion utilisateur
    const handleUserLogin = () => {
      console.log('UchroniaOS - Connexion détectée, restauration des fenêtres dans 3 secondes...');
      restoreWindows();
    };

    // Vérifier immédiatement si l'utilisateur est déjà connecté
    const checkExistingSession = () => {
      const saved = localStorage.getItem('ALBION_SAVE_DATA');
      if (saved) {
        try {
          const data = JSON.parse(saved);
          if (data.user) {
            console.log('UchroniaOS - Session utilisateur existante détectée, restauration immédiate');
            restoreWindows();
            return true;
          }
        } catch (e) {
          console.error('Erreur lors de la vérification de session:', e);
        }
      }
      return false;
    };

    // Vérifier s'il y a déjà une session active
    if (!checkExistingSession()) {
      // Pas de session active, écouter l'événement de connexion
      window.addEventListener('USER_SESSION_CHANGED', handleUserLogin);
    }

    return () => {
      window.removeEventListener('USER_SESSION_CHANGED', handleUserLogin);
    };
  }, []);

  // Persistence Logic - Save
  React.useEffect(() => {
    // Éviter de sauvegarder l'état vide au tout premier rendu avant le useEffect de chargement
    // ou pendant les 4 premières secondes après la connexion (temps de restauration)
    if (openWindows.length === 0 && !activeWindow) return;

    // Ajouter un délai pour éviter de sauvegarder pendant la phase de restauration
    const saveTimeout = setTimeout(() => {
      const existing = localStorage.getItem('ALBION_SAVE_DATA');
      let data = existing ? JSON.parse(existing) : {};

      data.windows = {
        open: openWindows,
        minimized: minimizedWindows,
        active: activeWindow,
        files: openFiles
      };

      localStorage.setItem('ALBION_SAVE_DATA', JSON.stringify(data));
      console.log('UchroniaOS - État sauvegardé:', data.windows);
    }, 500); // Délai de 500ms pour éviter les sauvegardes pendant la restauration

    return () => clearTimeout(saveTimeout);
  }, [openWindows, minimizedWindows, activeWindow, openFiles]);

  // Sauvegarde manuelle pour le développement
  const saveGameState = () => {
    const existing = localStorage.getItem('ALBION_SAVE_DATA');
    let data = existing ? JSON.parse(existing) : {};

    data.windows = {
      open: openWindows,
      minimized: minimizedWindows,
      active: activeWindow,
      files: openFiles
    };

    localStorage.setItem('ALBION_SAVE_DATA', JSON.stringify(data));

    // Notification de sauvegarde
    window.dispatchEvent(new CustomEvent('SCANNER_LOG', {
      detail: { message: t('systemLogs.manualSave') }
    }));
  };

  // Réinitialisation complète pour le développement
  const resetGameState = () => {
    // Sauvegarder le profil utilisateur avant reset
    const savedData = localStorage.getItem('ALBION_SAVE_DATA');
    let userProfile = null;

    if (savedData) {
      try {
        const data = JSON.parse(savedData);
        userProfile = data.user; // Sauvegarder le profil agent
      } catch (e) {
        console.error('Erreur lors de la sauvegarde du profil:', e);
      }
    }

    // Supprimer toutes les données de sauvegarde
    localStorage.removeItem('ALBION_SAVE_DATA');

    // Supprimer les données du tableau d'enquête
    localStorage.removeItem('board_items');
    localStorage.removeItem('board_document_groups');
    localStorage.removeItem('board_connections');
    localStorage.removeItem('board_history');
    localStorage.removeItem('board_bookmarks');

    // Supprimer les données du bloc-notes
    localStorage.removeItem('uchronia-notebook');
    localStorage.removeItem('uchronia-notebook-position');
    localStorage.removeItem('uchronia-notebook-open');

    // Supprimer les données des fenêtres (positions)
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith('window_rect_')) {
        localStorage.removeItem(key);
      }
    });

    // Supprimer les autres données de jeu
    localStorage.removeItem('wallpaper');
    localStorage.removeItem('ALBION_ICON_SIZE');
    localStorage.removeItem('ALBION_DESKTOP_ICONS');
    localStorage.removeItem('ALBION_DESKTOP_SHORTCUTS');
    localStorage.removeItem('ALBION_GAME_SETTINGS');
    localStorage.removeItem('ALBION_SETTINGS_INITIALIZED');
    localStorage.removeItem('tutorial_step'); // Legacy
    localStorage.removeItem('albion_user'); // Legacy

    // Restaurer le profil utilisateur si il existait
    if (userProfile) {
      const newData = { user: userProfile };
      localStorage.setItem('ALBION_SAVE_DATA', JSON.stringify(newData));

      // Log pour confirmer la préservation du profil
      console.log('Profil agent préservé lors du reset:', userProfile);
    }

    window.location.reload();
  };

  // Séquence de shutdown progressive : inverse de l'ouverture
  const startShutdownSequence = async () => {
    setIsShuttingDownSequence(true);
    setStartMenu(false);
    setDesktopFadingOut(true); // Activer le fondu de fermeture

    // 0. Arrêter la musique immédiatement
    window.dispatchEvent(new CustomEvent('ALBION_PLAYER_STOP'));

    // 1. Faire disparaître les widgets en fondu (horloge, notes, logs) - 1 seconde
    setWidgetsVisible(false);
    await new Promise(resolve => setTimeout(resolve, 1000));

    // 2. Faire disparaître le header + fenêtres + barre des tâches - 1 seconde
    // (géré par desktopFadingOut dans les classes CSS)
    await new Promise(resolve => setTimeout(resolve, 1000));

    // 3. Faire disparaître les icônes - 1 seconde
    await new Promise(resolve => setTimeout(resolve, 1000));

    // 4. Faire disparaître le fond d'écran - 1 seconde
    await new Promise(resolve => setTimeout(resolve, 1000));

    // 5. Lancer l'écran de shutdown
    setIsShuttingDownSequence(false);
    window.dispatchEvent(new CustomEvent('SYSTEM_SHUTDOWN_REQUESTED'));
  };

  // Séquence de redémarrage : identique au shutdown mais avec flag isRestart
  const startRestartSequence = async () => {
    setIsShuttingDownSequence(true);
    setStartMenu(false);
    setDesktopFadingOut(true); // Activer le fondu de fermeture

    // 0. Arrêter la musique immédiatement
    window.dispatchEvent(new CustomEvent('ALBION_PLAYER_STOP'));

    // Log système pour l'immersion
    window.dispatchEvent(new CustomEvent('SCANNER_LOG', {
      detail: { message: t('systemLogs.restartRequested') }
    }));

    // 1. Faire disparaître les widgets en fondu (horloge, notes, logs) - 1 seconde
    setWidgetsVisible(false);
    await new Promise(resolve => setTimeout(resolve, 1000));

    // 2. Faire disparaître le header + fenêtres + barre des tâches - 1 seconde
    await new Promise(resolve => setTimeout(resolve, 1000));

    // 3. Faire disparaître les icônes - 1 seconde
    await new Promise(resolve => setTimeout(resolve, 1000));

    // 4. Faire disparaître le fond d'écran - 1 seconde
    await new Promise(resolve => setTimeout(resolve, 1000));

    // 5. Lancer l'écran de shutdown avec flag restart
    setIsShuttingDownSequence(false);
    window.dispatchEvent(new CustomEvent('SYSTEM_SHUTDOWN_REQUESTED', {
      detail: { isRestart: true }
    }));
  };

  // Fonctions de gestion du dialogue de sauvegarde
  const handleQuitRequest = () => {
    // Vérifier si l'utilisateur a demandé à ne plus afficher la confirmation
    const skipConfirm = localStorage.getItem('ALBION_SKIP_SHUTDOWN_CONFIRM') === 'true';
    
    if (skipConfirm) {
      // Quitter directement sans confirmation
      window.dispatchEvent(new CustomEvent('SCANNER_LOG', {
        detail: { message: t('systemLogs.shutdownAuthorized') }
      }));
      startShutdownSequence();
      return;
    }
    
    // Vérifier s'il y a des données non sauvegardées
    const hasUnsavedData = checkForUnsavedData();

    if (hasUnsavedData) {
      // Log système pour l'immersion
      window.dispatchEvent(new CustomEvent('SCANNER_LOG', {
        detail: { message: t('systemLogs.shutdownRequested') }
      }));

      setIsSaveDialogOpen(true);
      setStartMenu(false);
    } else {
      // Pas de données non sauvegardées, quitter directement
      window.dispatchEvent(new CustomEvent('SCANNER_LOG', {
        detail: { message: t('systemLogs.shutdownAuthorized') }
      }));
      startShutdownSequence();
    }
  };

  const checkForUnsavedData = (): boolean => {
    // Vérifier s'il y a des éléments sur le tableau d'enquête
    try {
      const boardItems = localStorage.getItem('board_items');
      const boardConnections = localStorage.getItem('board_connections');

      if (boardItems) {
        const items = JSON.parse(boardItems);
        if (items && items.length > 0) return true;
      }
      
      if (boardConnections) {
        const connections = JSON.parse(boardConnections);
        if (connections && connections.length > 0) return true;
      }
    } catch (e) {
      console.error('Error checking board data:', e);
    }

    // Vérifier s'il y a des fenêtres ouvertes avec du contenu
    const contentWindows = openWindows.filter(w => w !== 'logs'); // logs n'est plus une fenêtre mais un widget
    if (contentWindows.length > 0) return true;

    // Vérifier s'il y a des messages non lus
    const unreadCount = getUnreadCount();
    if (unreadCount > 0) return true;

    // Toujours afficher la confirmation si une session est active
    // (l'utilisateur a fait quelque chose dans le système)
    const hasActiveSession = localStorage.getItem('ALBION_SAVE_DATA') !== null;
    if (hasActiveSession) return true;

    return false;
  };

  const handleSaveAndQuit = () => {
    window.dispatchEvent(new CustomEvent('SCANNER_LOG', {
      detail: { message: t('systemLogs.autoSaveInitiated') }
    }));

    saveGameState();
    setIsSaveDialogOpen(false);

    // Petit délai pour laisser la sauvegarde se terminer
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent('SCANNER_LOG', {
        detail: { message: t('systemLogs.saveCompleted') }
      }));
      startShutdownSequence();
    }, 500);
  };

  const handleSaveAndVisitCommunity = () => {
    window.dispatchEvent(new CustomEvent('SCANNER_LOG', {
      detail: { message: t('systemLogs.autoSaveInitiated') }
    }));

    saveGameState();
    
    // Marquer qu'on doit ouvrir le lien Facebook après la fermeture
    localStorage.setItem('ALBION_OPEN_COMMUNITY_LINK', 'true');
    
    setIsSaveDialogOpen(false);

    // Petit délai pour laisser la sauvegarde se terminer
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent('SCANNER_LOG', {
        detail: { message: t('systemLogs.saveCompleted') }
      }));
      startShutdownSequence();
    }, 500);
  };

  const handleSaveAndVisitItch = () => {
    window.dispatchEvent(new CustomEvent('SCANNER_LOG', {
      detail: { message: t('systemLogs.autoSaveInitiated') }
    }));

    saveGameState();
    
    // Marquer qu'on doit ouvrir le lien Itch.io après la fermeture
    localStorage.setItem('ALBION_OPEN_ITCH_LINK', 'true');
    
    setIsSaveDialogOpen(false);

    // Petit délai pour laisser la sauvegarde se terminer
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent('SCANNER_LOG', {
        detail: { message: t('systemLogs.saveCompleted') }
      }));
      startShutdownSequence();
    }, 500);
  };

  const handleQuitWithoutSaving = () => {
    window.dispatchEvent(new CustomEvent('SCANNER_LOG', {
      detail: { message: t('systemLogs.forcedShutdown') }
    }));

    setIsSaveDialogOpen(false);
    startShutdownSequence();
  };

  const handleCancelQuit = () => {
    window.dispatchEvent(new CustomEvent('SCANNER_LOG', {
      detail: { message: t('systemLogs.shutdownCancelled') }
    }));

    setIsSaveDialogOpen(false);
  };

  // Initialisation progressive du bureau avec animations
  React.useEffect(() => {
    const initializeDesktop = () => {
      // Délai pour laisser le temps au composant de se monter
      setTimeout(() => {
        setDesktopInitialized(true);

        // Log de démarrage système
        window.dispatchEvent(new CustomEvent('SCANNER_LOG', {
          detail: { message: t('systemLogs.environmentInitialized') }
        }));

        // Faire apparaître les widgets après le header et les fenêtres (3 secondes après le début)
        setTimeout(() => {
          setWidgetsVisible(true);
        }, 3000);

        // Organiser automatiquement les icônes au premier lancement
        // (si aucune position personnalisée n'est sauvegardée)
        const savedPositions = localStorage.getItem('ALBION_DESKTOP_ICONS');
        if (!savedPositions) {
          console.log('UchroniaOS - Premier lancement, organisation automatique des icônes');
          // Petit délai pour laisser les icônes s'afficher
          setTimeout(() => {
            handleAutoArrangeIcons();
          }, 500);
        }
      }, 300);
    };

    initializeDesktop();
  }, []);

  // Le tutoriel démarre automatiquement 5 secondes après l'arrivée sur le bureau
  // Voir useTutorial.ts pour la logique

  // Image Enhancer State
  const [enhancedImage, setEnhancedImage] = useState<string | null>(null);
  const [enhancerWindowSize, setEnhancerWindowSize] = useState<{ width: number; height: number }>({ width: 600, height: 500 });

  const [taskbarMenu, setTaskbarMenu] = useState<{ winId: string; x: number; y: number } | null>(null);
  const [startMenu, setStartMenu] = useState(false);
  const [iconContextMenu, setIconContextMenu] = useState<{ iconName: string; x: number; y: number } | null>(null);

  // État pour la fenêtre d'aide générique
  const [helpWindow, setHelpWindow] = useState<{ windowId: string; title: string } | null>(null);
  // File Viewer Event Listener
  React.useEffect(() => {
    const handleOpenFile = (e: Event) => {
      const customEvent = e as CustomEvent;
      const fileId = customEvent.detail.fileId;
      if (FILE_CONTENTS[fileId]) {
        setOpenFiles(prev => {
          // Si le fichier est déjà ouvert dans un viewer, on focus simplement cet exemplaire
          const existing = prev.find(f => f.fileId === fileId);
          if (existing) {
            setActiveWindow(existing.id);
            setMinimizedWindows(m => m.filter(w => w !== existing.id));
            // Mettre à jour la pile
            setWindowStack(prevStack => {
              const newStack = prevStack.filter(w => w !== existing.id);
              return [...newStack, existing.id];
            });
            return prev;
          }
          const instanceId = `viewer_${fileId}_${Date.now()}`;
          openWindowWithAnimation(instanceId);

          // Émettre l'événement FILE_OPENED pour le tutoriel
          window.dispatchEvent(new CustomEvent('FILE_OPENED', {
            detail: { fileId }
          }));

          return [...prev, { id: instanceId, fileId }];
        });
      }
    };
    window.addEventListener('OPEN_FILE', handleOpenFile);
    return () => window.removeEventListener('OPEN_FILE', handleOpenFile);
  }, []);

  // Tutorial Events Listeners - Désactivés pour la démo
  React.useEffect(() => {
    // Dans la démo, pas d'événements tutoriel complexes
    // Les fonctionnalités sont accessibles librement

    // Garder juste les event listeners de base pour ne pas casser le code
    const handleSuperposition = () => {
      // Pas d'action spécifique dans la démo
    };

    const handleBoardLink = () => {
      // Pas d'action spécifique dans la démo
    };

    const handleEarOpened = () => {
      // Pas d'action spécifique dans la démo
    };

    window.addEventListener('TUTORIAL_SUPERPOSITION_DETECTION', handleSuperposition);
    window.addEventListener('BOARD_LINK_CREATED', handleBoardLink);
    window.addEventListener('EAR_OPENED', handleEarOpened);

    return () => {
      window.removeEventListener('TUTORIAL_SUPERPOSITION_DETECTION', handleSuperposition);
      window.removeEventListener('BOARD_LINK_CREATED', handleBoardLink);
      window.removeEventListener('EAR_OPENED', handleEarOpened);
    };
  }, []);

  // Le certificat sera maintenant découvert via les notifications
  // Plus d'ouverture automatique - meilleure expérience utilisateur
  React.useEffect(() => {
    // Écouter l'événement FILE_OPENED pour le certificat (ouverture manuelle)
    const handleFileOpened = (e: Event) => {
      const customEvent = e as CustomEvent;
      const { fileId } = customEvent.detail;
      
      // Si c'est le certificat qui est ouvert, émettre la certification
      if (fileId === 'f_certificat') {
        // Vérifier si l'utilisateur est déjà certifié
        const savedData = localStorage.getItem('ALBION_SAVE_DATA');
        let isAlreadyCertified = false;
        
        if (savedData) {
          try {
            const data = JSON.parse(savedData);
            isAlreadyCertified = data.certification && data.certification.completed;
          } catch (e) {
            console.error('Error checking certification status:', e);
          }
        }
        
        console.log('📜 Certificat ouvert - Certification complétée');
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent('CERTIFICATION_COMPLETED'));
          console.log('🎓 Certification complétée - Badge mis à jour');
          
          // Envoyer une notification pour le nouveau fond d'écran SEULEMENT si pas déjà certifié
          if (!isAlreadyCertified) {
            setTimeout(() => {
              window.dispatchEvent(new CustomEvent('NEW_FILE_NOTIFICATION', {
                detail: {
                  fileName: 'Fond d\'écran de certification',
                  source: 'SYSTÈME',
                  id: `wallpaper_cert_${Date.now()}`
                }
              }));
              console.log('📱 Notification envoyée - Nouveau fond d\'écran disponible');
            }, 2000);
          } else {
            console.log('📱 Notification ignorée - Utilisateur déjà certifié');
          }
        }, 500);
      }
    };

    window.addEventListener('FILE_OPENED', handleFileOpened);

    return () => {
      window.removeEventListener('FILE_OPENED', handleFileOpened);
    };
  }, []);

  // Image Enhancer Event Listener
  React.useEffect(() => {
    const handleEnhance = (e: Event) => {
      const customEvent = e as CustomEvent;
      const imageUrl = customEvent.detail.img;

      // Créer une image temporaire pour obtenir ses dimensions
      const img = new Image();
      img.onload = () => {
        // Calculer les dimensions de la fenêtre en fonction de l'image
        const maxWidth = window.innerWidth * 0.9; // 90% de la largeur de l'écran
        const maxHeight = window.innerHeight * 0.9; // 90% de la hauteur de l'écran

        // Hauteur de l'interface : toolbar (32px) + status bar (24px) + marges
        const interfaceHeight = 32 + 24 + 20; // Total ~76px
        const interfaceWidth = 0; // Pas de marge supplémentaire, l'image s'adapte au conteneur

        let windowWidth = img.naturalWidth;
        let windowHeight = img.naturalHeight + interfaceHeight;

        // Calculer le ratio de l'image pour maintenir les proportions
        const imageRatio = img.naturalWidth / img.naturalHeight;

        // Limiter aux dimensions maximales en conservant le ratio
        if (windowWidth > maxWidth || windowHeight > maxHeight) {
          const widthRatio = maxWidth / windowWidth;
          const heightRatio = maxHeight / windowHeight;
          const ratio = Math.min(widthRatio, heightRatio);

          windowWidth = windowWidth * ratio;
          windowHeight = windowHeight * ratio;
        }

        // Dimensions minimales
        windowWidth = Math.max(450, windowWidth);
        windowHeight = Math.max(350, windowHeight);

        console.log(`Image dimensions: ${img.naturalWidth}x${img.naturalHeight}`);
        console.log(`Window dimensions: ${Math.round(windowWidth)}x${Math.round(windowHeight)}`);

        // Stocker les dimensions calculées pour la fenêtre enhancer
        setEnhancerWindowSize({ width: Math.round(windowWidth), height: Math.round(windowHeight) });
        setEnhancedImage(imageUrl);
        openWindowWithAnimation('enhancer');
      };

      img.src = imageUrl;
    };

    const handleIconContextMenu = (e: Event) => {
      const customEvent = e as CustomEvent;
      setIconContextMenu({
        iconName: customEvent.detail.iconName,
        x: customEvent.detail.x,
        y: customEvent.detail.y
      });
    };

    const handleWindowHelp = (e: Event) => {
      const customEvent = e as CustomEvent;
      const windowId = customEvent.detail.windowId;
      const config = getWindowConfig(windowId);
      setHelpWindow({ windowId, title: config.title });
    };

    const handleAlbionPlayerResize = (e: Event) => {
      const customEvent = e as CustomEvent;
      const newWidth = customEvent.detail.width;
      
      // Mettre à jour la configuration de la fenêtre albion-player
      setWindowConfigs(prev => ({
        ...prev,
        'albion-player': {
          ...prev['albion-player'],
          width: newWidth
        }
      }));

      // Mettre à jour directement le localStorage pour forcer le Window à se redimensionner
      const savedRect = localStorage.getItem('window_rect_albion-player');
      if (savedRect) {
        try {
          const rect = JSON.parse(savedRect);
          rect.width = newWidth;
          // S'assurer que la hauteur est suffisante pour la playlist
          if (rect.height < 650) {
            rect.height = 650;
          }
          localStorage.setItem('window_rect_albion-player', JSON.stringify(rect));
          
          // Forcer le re-render en émettant un événement personnalisé
          window.dispatchEvent(new CustomEvent('FORCE_WINDOW_UPDATE', {
            detail: { windowId: 'albion-player' }
          }));
        } catch (e) {
          console.error('Erreur mise à jour rect albion-player:', e);
        }
      } else {
        // Créer un nouveau rect si il n'existe pas
        const newRect = {
          x: 100,
          y: 100,
          width: newWidth,
          height: 650 // Hauteur correcte pour inclure la playlist
        };
        localStorage.setItem('window_rect_albion-player', JSON.stringify(newRect));
        
        // Forcer le re-render
        window.dispatchEvent(new CustomEvent('FORCE_WINDOW_UPDATE', {
          detail: { windowId: 'albion-player' }
        }));
      }
    };

    const handleAddDesktopShortcut = (e: Event) => {
      const customEvent = e as CustomEvent;
      const { name, type, fileId, isImage } = customEvent.detail;

      // Trouver une position libre sur le bureau
      const existingPositions = Object.values(desktopIcons);
      let newX = 40;
      let newY = 40;

      // Fonction pour vérifier si une position est occupée
      const isPositionOccupied = (x: number, y: number) =>
        existingPositions.some(pos => pos.x === x && pos.y === y);

      // Chercher une position libre en colonne
      while (isPositionOccupied(newX, newY)) {
        newY += 100;
        if (newY > window.innerHeight - 200) {
          newY = 40;
          newX += 120;
        }
      }

      // Ajouter l'icône sur le bureau
      setDesktopIcons(prev => ({
        ...prev,
        [name]: { x: newX, y: newY }
      }));

      // Stocker les métadonnées du raccourci pour l'affichage
      const shortcutData = {
        name,
        type,
        fileId,
        isImage,
        isShortcut: true
      };

      // Sauvegarder dans localStorage pour persistance
      const existingShortcuts = JSON.parse(localStorage.getItem('ALBION_DESKTOP_SHORTCUTS') || '{}');
      existingShortcuts[name] = shortcutData;
      localStorage.setItem('ALBION_DESKTOP_SHORTCUTS', JSON.stringify(existingShortcuts));
      setDesktopShortcuts(existingShortcuts);
    };

    const handleCloseWindow = (e: Event) => {
      const customEvent = e as CustomEvent;
      const { windowId } = customEvent.detail;
      closeWindow(windowId);
    };

    const handleKernelCrash = () => {
      // L'événement est maintenant géré dans App.tsx
    };

    const handleCloseDesktopContextMenu = () => {
      setDesktopContextMenu(null);
      setIconContextMenu(null);
    };

    const handleRestoreWindow = (e: Event) => {
      const customEvent = e as CustomEvent;
      const { windowId } = customEvent.detail;
      
      console.log('handleRestoreWindow appelé avec windowId:', windowId);
      console.log('minimizedWindows:', minimizedWindows);
      
      if (windowId && minimizedWindows.includes(windowId)) {
        console.log('Restauration de la fenêtre:', windowId);
        setMinimizedWindows(prev => prev.filter(w => w !== windowId));
        setActiveWindow(windowId);
        
        // Mettre à jour la pile : déplacer la fenêtre en haut
        setWindowStack(prev => {
          const newStack = prev.filter(w => w !== windowId);
          return [...newStack, windowId];
        });
      } else {
        console.log('Fenêtre non trouvée dans minimizedWindows ou windowId invalide');
      }
    };

    const handleOpenExplorerAndHighlight = (e: Event) => {
      const customEvent = e as CustomEvent;
      const { fileId } = customEvent.detail;
      
      // Ouvrir l'explorateur s'il n'est pas déjà ouvert
      if (!openWindows.includes('explorer')) {
        openWindowWithAnimation('explorer');
      } else {
        // Si déjà ouvert, le remettre au premier plan
        setActiveWindow('explorer');
        setWindowStack(prev => {
          const newStack = prev.filter(w => w !== 'explorer');
          return [...newStack, 'explorer'];
        });
      }
      
      // Émettre un événement pour que l'explorateur mette en évidence le fichier
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('HIGHLIGHT_FILE_IN_EXPLORER', {
          detail: { fileId }
        }));
      }, 100); // Petit délai pour s'assurer que l'explorateur est chargé
    };

    const handleOpenWallpaperSelector = () => {
      // Ouvrir le sélecteur de fond d'écran
      if (!openWindows.includes('wallpaper-selector')) {
        openWindowWithAnimation('wallpaper-selector');
      } else {
        // Si déjà ouvert, le remettre au premier plan
        setActiveWindow('wallpaper-selector');
        setWindowStack(prev => {
          const newStack = prev.filter(w => w !== 'wallpaper-selector');
          return [...newStack, 'wallpaper-selector'];
        });
      }
      console.log('🖼️ Sélecteur de fond d\'écran ouvert via notification');
    };

    // Handler pour la transition de fond d'écran
    const handleWallpaperTransition = (e: Event) => {
      const customEvent = e as CustomEvent;
      const newType = customEvent.detail.newType;
      console.log('🎨 Début de transition wallpaper vers:', newType);
      
      // Démarrer la transition
      setWallpaperTransitioning(true);
      setNextWallpaperType(newType);
      
      // Après l'animation de fondu sortant, changer le wallpaper
      setTimeout(() => {
        setWallpaperType(newType);
        localStorage.setItem('wallpaperType', newType);
        
        // Après un court délai, terminer la transition
        setTimeout(() => {
          setWallpaperTransitioning(false);
          setNextWallpaperType(null);
        }, 100);
      }, 500); // Durée du fondu sortant
    };

    window.addEventListener('ENHANCE_IMAGE', handleEnhance);
    window.addEventListener('ICON_CONTEXT_MENU', handleIconContextMenu);
    window.addEventListener('CLOSE_DESKTOP_CONTEXT_MENU', handleCloseDesktopContextMenu);
    window.addEventListener('SHOW_WINDOW_HELP', handleWindowHelp);
    window.addEventListener('ALBION_PLAYER_RESIZE', handleAlbionPlayerResize);
    window.addEventListener('ADD_DESKTOP_SHORTCUT', handleAddDesktopShortcut);
    window.addEventListener('CLOSE_WINDOW', handleCloseWindow);
    window.addEventListener('RESTORE_WINDOW', handleRestoreWindow);
    window.addEventListener('OPEN_EXPLORER_AND_HIGHLIGHT', handleOpenExplorerAndHighlight);
    window.addEventListener('OPEN_WALLPAPER_SELECTOR', handleOpenWallpaperSelector);
    window.addEventListener('WALLPAPER_TRANSITION_START', handleWallpaperTransition as EventListener);
    window.addEventListener('KERNEL_CRASH', handleKernelCrash);
    return () => {
      window.removeEventListener('ENHANCE_IMAGE', handleEnhance);
      window.removeEventListener('ICON_CONTEXT_MENU', handleIconContextMenu);
      window.removeEventListener('SHOW_WINDOW_HELP', handleWindowHelp);
      window.removeEventListener('ALBION_PLAYER_RESIZE', handleAlbionPlayerResize);
      window.removeEventListener('ADD_DESKTOP_SHORTCUT', handleAddDesktopShortcut);
      window.removeEventListener('CLOSE_WINDOW', handleCloseWindow);
      window.removeEventListener('RESTORE_WINDOW', handleRestoreWindow);
      window.removeEventListener('OPEN_EXPLORER_AND_HIGHLIGHT', handleOpenExplorerAndHighlight);
      window.removeEventListener('OPEN_WALLPAPER_SELECTOR', handleOpenWallpaperSelector);
      window.removeEventListener('WALLPAPER_TRANSITION_START', handleWallpaperTransition as EventListener);
      window.removeEventListener('KERNEL_CRASH', handleKernelCrash);
    };
  }, [desktopIcons]);

  const toggleWindow = (windowName: string) => {
    if (openWindows.includes(windowName)) {
      if (minimizedWindows.includes(windowName)) {
        setMinimizedWindows(m => m.filter(w => w !== windowName));
        setActiveWindow(windowName);
        // Mettre à jour la pile
        setWindowStack(prev => {
          const newStack = prev.filter(w => w !== windowName);
          return [...newStack, windowName];
        });
        // Notifier l'ouverture du messenger
        if (windowName === 'messenger') {
          setMessengerOpen(true);
        }
      } else {
        setActiveWindow(windowName);
        // Mettre à jour la pile
        setWindowStack(prev => {
          const newStack = prev.filter(w => w !== windowName);
          return [...newStack, windowName];
        });
      }
    } else {
      // Nouvelle fenêtre - utiliser l'animation
      openWindowWithAnimation(windowName);
      // Notifier l'ouverture du messenger
      if (windowName === 'messenger') {
        setMessengerOpen(true);
      }
    }
  };

  const closeWindow = (windowName: string) => {
    // Retirer la fenêtre de la pile
    setWindowStack(prev => prev.filter(w => w !== windowName));

    // Nettoyer le cache de configuration pour cette fenêtre
    setWindowConfigs(prev => {
      const newConfigs = { ...prev };
      delete newConfigs[windowName];
      return newConfigs;
    });

    // Délai spécial pour le tableau pour permettre la sauvegarde complète
    if (windowName === 'board') {
      // Forcer une sauvegarde immédiate avant fermeture
      window.dispatchEvent(new CustomEvent('BOARD_SAVE_STATE'));

      // Sauvegarder aussi la position de la fenêtre explicitement
      const windowRect = localStorage.getItem(`window_rect_${windowName}`);
      if (windowRect) {
        // La position est déjà sauvegardée, on force juste une dernière sauvegarde
        console.log('Position fenêtre tableau sauvegardée:', windowRect);
      }

      // Délai de 100ms pour permettre la sauvegarde
      setTimeout(() => {
        setOpenWindows(prev => prev.filter(w => w !== windowName));
        setMinimizedWindows(prev => prev.filter(w => w !== windowName));
        // Ne pas supprimer la sauvegarde de position pour permettre la persistance

        if (windowName.startsWith('viewer_')) {
          setOpenFiles(prev => prev.filter(f => f.id !== windowName));
        }

        if (windowName.startsWith('properties_')) {
          setPropertiesIcon(null);
        }

        setActiveWindow(current => current === windowName ? null : current);
      }, 100);
    } else {
      // Fermeture immédiate pour les autres fenêtres
      setOpenWindows(prev => prev.filter(w => w !== windowName));
      setMinimizedWindows(prev => prev.filter(w => w !== windowName));
      // Ne pas supprimer la sauvegarde de position pour permettre la persistance

      if (windowName.startsWith('viewer_')) {
        setOpenFiles(prev => prev.filter(f => f.id !== windowName));
      }

      if (windowName.startsWith('properties_')) {
        setPropertiesIcon(null);
      }

      setActiveWindow(current => current === windowName ? null : current);

      // Actions spécifiques selon le type de fenêtre
      if (windowName === 'messenger') {
        setMessengerOpen(false);
      }
      if (windowName === 'enhancer') {
        setEnhancedImage(null);
      }
    }
  };

  const minimizeWindow = (windowName: string) => {
    setMinimizedWindows(prev => [...prev, windowName]);
    setActiveWindow(null);
  };


  // Context Menu Handler
  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    const menuWidth = 200;
    const menuHeight = 150;
    const x = e.clientX + menuWidth > window.innerWidth ? e.clientX - menuWidth : e.clientX;
    const y = e.clientY + menuHeight > window.innerHeight ? e.clientY - menuHeight : e.clientY;
    setDesktopContextMenu({ x, y });
  };

  const handleDesktopClick = () => {
    if (contextMenu) setContextMenu(null);
    if (desktopContextMenu) setDesktopContextMenu(null);
    setSelectedIcon(null); // Désélectionner l'icône quand on clique sur le bureau
    setIsProcessMonitorOpen(false); // Fermer le moniteur de processus
  };

  const handleWallpaperChange = (type: 'default' | 'certification') => {
    setWallpaperType(type);
    localStorage.setItem('wallpaperType', type);
    setDesktopContextMenu(null);
  };

  const handleOpenWallpaperSelector = () => {
    openWindowWithAnimation('wallpaper-selector');
    setDesktopContextMenu(null);
  };

  const handleResetWallpaper = () => {
    setWallpaperType('default');
    localStorage.setItem('wallpaperType', 'default');
    setContextMenu(null);
  };

  // Fonctions pour la gestion des icônes du bureau
  const handleIconSizeChange = (size: 'small' | 'medium' | 'large') => {
    setIconSize(size);
    setDesktopContextMenu(null);
  };

  const handleAutoArrangeIcons = () => {
    // Filtrer LOGS_SYS car c'est maintenant un widget, pas une icône
    const iconNames = Object.keys(desktopIcons).filter(name => name !== 'LOGS_SYS');
    const newPositions: Record<string, { x: number; y: number }> = {};

    // Calculer combien d'icônes peuvent tenir verticalement
    const availableHeight = window.innerHeight - 150; // Espace pour header et taskbar
    const iconSpacing = 100;
    const startY = 40;
    const maxIconsPerColumn = Math.floor((availableHeight - startY) / iconSpacing);

    iconNames.forEach((name, index) => {
      const col = Math.floor(index / maxIconsPerColumn); // Colonne actuelle
      const row = index % maxIconsPerColumn; // Position dans la colonne
      newPositions[name] = {
        x: 40 + col * 120, // Espacement horizontal entre colonnes
        y: startY + row * iconSpacing  // Espacement vertical dans la colonne
      };
    });

    setDesktopIcons(newPositions);
    setDesktopContextMenu(null);
  };

  const moveIcon = (iconName: string, newX: number, newY: number) => {
    setDesktopIcons((prev: Record<string, { x: number; y: number }>) => ({
      ...prev,
      [iconName]: { x: newX, y: newY }
    }));
  };
  // Fonction pour obtenir l'aperçu d'un fichier
  const getFilePreview = (fileId: string): string => {
    // Importer FILE_CONTENTS pour accéder aux données des fichiers
    const { FILE_CONTENTS } = require('./data/fileSystem');
    const fileContent = FILE_CONTENTS[fileId];
    if (fileContent && fileContent.type === 'image' && fileContent.content) {
      return fileContent.content as string;
    }
    // Retourner une image par défaut ou une icône
    return 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzIiIGhlaWdodD0iMzIiIHZpZXdCb3g9IjAgMCAzMiAzMiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjMyIiBoZWlnaHQ9IjMyIiBmaWxsPSIjMzMzIi8+CjxwYXRoIGQ9Ik04IDhIMjRWMjRIOFY4WiIgc3Ryb2tlPSIjNjY2IiBzdHJva2Utd2lkdGg9IjIiLz4KPC9zdmc+';
  };

  // Fonction pour ouvrir un fichier depuis un raccourci
  const handleOpenFile = (fileId: string) => {
    const event = new CustomEvent('OPEN_FILE', { detail: { fileId } });
    window.dispatchEvent(event);
  };

  // Fonctions pour le menu contextuel des icônes
  const handleOpenIcon = (iconName: string) => {
    // Trouver l'action correspondante à l'icône
    switch (iconName) {
      case 'TABLEAU':
        toggleWindow('board');
        break;
      case 'FICHIERS':
        toggleWindow('explorer');
        break;
      case 'LOGS_SYS':
        setIsLogsWidgetVisible(prev => !prev); // Toggle la visibilité du widget au lieu d'ouvrir une fenêtre
        break;
      case 'MESSAGER':
        toggleWindow('messenger');
        break;
      case 'L\'OREILLE':
        toggleWindow('theear');
        window.dispatchEvent(new Event('EAR_OPENED'));
        break;
      case 'MA_STATION':
        // Pas d'action pour le moment
        break;
      case 'CORBEILLE':
        // Pas d'action pour le moment
        break;
    }
    setIconContextMenu(null);
  };

  const handleDeleteIcon = (iconName: string) => {
    // Supprimer l'icône du bureau (la cacher)
    setDesktopIcons((prev: Record<string, { x: number; y: number }>) => {
      const newIcons = { ...prev };
      delete newIcons[iconName];
      return newIcons;
    });
    setIconContextMenu(null);
  };

  const handleIconProperties = (iconName: string) => {
    // Ouvrir la fenêtre de propriétés
    setPropertiesIcon(iconName);
    const propertiesWindowId = `properties_${iconName}`;
    openWindowWithAnimation(propertiesWindowId);
    setIconContextMenu(null);
  };


  // Interface pour la configuration des fenêtres
  interface WindowConfig {
    title: string;
    width: number;
    height: number;
    x: number;
    y: number;
  }

  // Cache des configurations de fenêtres pour éviter les recalculs constants
  const [windowConfigs, setWindowConfigs] = useState<Record<string, WindowConfig>>({});

  // Vider le cache des configurations quand la langue change pour forcer la re-traduction des titres
  React.useEffect(() => {
    const handleLanguageChange = () => {
      console.log('UchroniaOS - Language changed, clearing window configs cache');
      setWindowConfigs({});
    };

    window.addEventListener('LANGUAGE_CHANGED', handleLanguageChange);
    return () => window.removeEventListener('LANGUAGE_CHANGED', handleLanguageChange);
  }, []);

  // Config des fenêtres initiales - mise en cache pour éviter les repositionnements
  const getWindowConfig = (id: string): WindowConfig => {
    // Si la configuration est déjà en cache, l'utiliser TOUJOURS
    if (windowConfigs[id]) {
      console.log(`getWindowConfig(${id}) - Configuration en cache utilisée:`, windowConfigs[id]);
      return windowConfigs[id];
    }

    console.log(`getWindowConfig(${id}) - Calcul d'une nouvelle configuration`);

    // Sinon, calculer la configuration une seule fois avec des positions FIXES
    // IMPORTANT: Ne plus utiliser window.innerWidth/innerHeight pour éviter les recalculs
    let config: WindowConfig;
    switch (id) {
      case 'board':
        config = {
          title: t('board.title'),
          width: 1200,
          height: 800,
          x: 100,
          y: 100
        };
        break;
      case 'explorer':
        config = { title: t('fileExplorer.title'), width: 300, height: 400, x: 1600, y: 80 };
        break;
      case 'enhancer':
        config = {
          title: 'UNITÉ_D_AMÉLIORATION_OPTIQUE',
          width: enhancerWindowSize.width,
          height: enhancerWindowSize.height,
          x: 300,
          y: 200
        };
        break;
      case 'messenger':
        config = {
          title: t('messenger.title'),
          width: 350,
          height: 450,
          x: 800,
          y: 300
        };
        break;
      case 'theear':
        config = { title: t('theEar.title'), width: 500, height: 400, x: 200, y: 200 };
        break;
      case 'station-profile':
        config = { title: t('stationProfile.title'), width: 500, height: 700, x: 700, y: 100 };
        break;
      case 'wallpaper-selector':
        config = { title: t('wallpaperSelector.title').toUpperCase(), width: 700, height: 500, x: 150, y: 100 };
        break;
      case 'snake':
        config = { title: t('snake.title'), width: 800, height: 820, x: 400, y: 100 };
        break;
      case 'spaceinvaders':
        config = { title: t('spaceInvaders.title'), width: 520, height: 640, x: 400, y: 100 };
        break;
      case 'albion-player':
        config = { title: 'WavePlayer v1.3', width: 350, height: 480, x: 1550, y: 100 };
        break;
      default:
        if (id.startsWith('viewer_')) {
          const fileInstance = openFiles.find(f => f.id === id);
          const title = fileInstance && FILE_CONTENTS[fileInstance.fileId] ? FILE_CONTENTS[fileInstance.fileId].title : 'VISIONNEUSE_DE_FICHIERS';

          // Ajuster les dimensions selon le type de fichier avec positions FIXES
          if (fileInstance && FILE_CONTENTS[fileInstance.fileId]) {
            const fileContent = FILE_CONTENTS[fileInstance.fileId];
            if (fileContent.type === 'image') {
              // Pour les images, dimensions fixes
              const offset = (openFiles.length * 20) % 100;
              // IMAGE A - ANALYSE OPTIQUE : dimensions spécifiques
              if (title.includes('IMAGE A')) {
                config = { title, width: 549, height: 764, x: 400 + offset, y: 150 + offset };
              } else if (title.includes('IMAGE B')) {
                config = { title, width: 561, height: 753, x: 738 + offset, y: 42 + offset };
              } else {
                config = { title, width: 650, height: 550, x: 400 + offset, y: 150 + offset };
              }
            } else {
              // Pour les documents texte, dimensions fixes
              const offset = (openFiles.length * 20) % 100;
              // Certificat de formation : fenêtre plus haute
              if (title === 'CERTIFICAT DE FORMATION') {
                config = { title, width: 1006, height: 930, x: 295 + offset, y: 40 + offset };
              } else if (title.includes('MANUEL OPÉRATEUR')) {
                config = { title, width: 650, height: 861, x: 584 + offset, y: 40 + offset };
              } else if (title.includes('GUIDE INTERFACE')) {
                config = { title, width: 815, height: 704, x: 484 + offset, y: 41 + offset };
              } else if (title.includes('PROCÉDURES AVANCÉES')) {
                config = { title, width: 808, height: 734, x: 514 + offset, y: 40 + offset };
              } else if (title.includes('ANALYSE OPTIQUE')) {
                config = { title, width: 693, height: 672, x: 615 + offset, y: 38 + offset };
              } else if (title.includes('README')) {
                config = { title, width: 699, height: 674, x: 557 + offset, y: 41 + offset };
              } else {
                config = { title, width: 450, height: 600, x: 200 + offset, y: 50 + offset };
              }
            }
          } else {
            // Dimensions par défaut pour les documents
            const offset = (openFiles.length * 20) % 100;
            // Certificat de formation : fenêtre plus haute
            if (title === 'CERTIFICAT DE FORMATION') {
              config = { title, width: 1006, height: 930, x: 295 + offset, y: 40 + offset };
            } else {
              config = { title, width: 450, height: 600, x: 200 + offset, y: 50 + offset };
            }
          }
        } else if (id.startsWith('properties_')) {
          const iconName = id.replace('properties_', '');
          config = { title: `PROPRIÉTÉS - ${iconName}`, width: 600, height: 700, x: 250, y: 50 };
        } else {
          config = { title: id.toUpperCase(), width: 400, height: 300, x: 150, y: 150 };
        }
    }

    console.log(`getWindowConfig(${id}) - Nouvelle configuration calculée:`, config);

    // Mettre en cache la configuration de manière PERMANENTE
    setWindowConfigs(prev => ({ ...prev, [id]: config }));
    return config;
  };

  return (
    <div
      ref={mainContainerRef}
      className={`h-screen w-screen bg-[#0f172a] overflow-hidden font-mono select-none flex flex-col relative text-slate-300 ${getStressClasses()} ${stressLevel.effects.fanNoise > 0.3 ? 'fan-noise' : ''} ${desktopInitialized ? 'desktop-initialized' : 'desktop-initializing'
        }`}
      style={{ ...getInputStyle(), position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, overscrollBehavior: 'none', overflowAnchor: 'none' }}
      onContextMenu={handleContextMenu}
      onClick={handleDesktopClick}
    >
      {/* SYSTEM HEADER - Apparaît avec les fenêtres */}
      <div className={`fixed top-0 left-0 w-full h-9 z-[100] transition-all duration-1000 ${
        desktopFadingOut 
          ? '-translate-y-full opacity-0' 
          : desktopInitialized 
            ? 'translate-y-0 opacity-100 delay-[2000ms]' 
            : '-translate-y-full opacity-0'
        }`}>
        <SystemHeader
          onSave={saveGameState}
          onReset={resetGameState}
          openWindows={openWindows}
          activeProcesses={notifications.length + messageNotifications.length}
          onCpuClick={() => setIsProcessMonitorOpen(!isProcessMonitorOpen)}
          cpuLoad={cpuLoad}
          memoryUsage={memoryUsage}
          notificationCount={unviewedNotificationCount}
          onNotificationClick={() => {
            if (isNotificationPanelOpen) {
              // Si ouvert, fermer directement
              setIsNotificationPanelOpen(false);
            } else {
              // Si fermé, ouvrir directement
              setIsNotificationPanelOpen(true);
            }
          }}
        />
      </div>



      {/* ESPACE DE TRAVAIL (DESKTOP) */}
      <div className="fixed top-9 left-0 right-0 bottom-10 z-10 bg-transparent"
        style={{
          overflow: 'clip',
          maxWidth: '100vw',
          height: 'calc(100vh - 76px)',
          position: 'fixed',
          overflowAnchor: 'none'
        }}>
        {/* BACKGROUND LAYER - Apparaît en premier avec un fondu */}
        <div className={`absolute inset-0 z-0 select-none pointer-events-none transition-opacity duration-500 ${
          desktopFadingOut 
            ? 'opacity-0 delay-[3000ms]' 
            : desktopInitialized 
              ? wallpaperTransitioning ? 'opacity-0' : 'opacity-100'
              : 'opacity-0'
          }`}>
          {wallpaperType === 'certification' ? (
            <CertificationWallpaper load={openWindows.length} />
          ) : (
            <SystemBlueprint load={openWindows.length} />
          )}
        </div>

        {/* BACKGROUND LAYER TRANSITION - Pour l'animation de transition */}
        {wallpaperTransitioning && nextWallpaperType && (
          <div className={`absolute inset-0 z-0 select-none pointer-events-none transition-opacity duration-500 opacity-0 animate-fade-in`}
               style={{ animationDelay: '500ms', animationFillMode: 'forwards' }}>
            {nextWallpaperType === 'certification' ? (
              <CertificationWallpaper load={openWindows.length} />
            ) : (
              <SystemBlueprint load={openWindows.length} />
            )}
          </div>
        )}

        {/* ICÔNES DU BUREAU (Positions personnalisées) - Apparaissent après le fond d'écran */}
        <div className={`absolute inset-0 z-[50] overflow-hidden transition-opacity duration-1000 ${
          desktopFadingOut 
            ? 'opacity-0 scale-95 delay-[2000ms]' 
            : desktopInitialized 
              ? 'opacity-100 scale-100 delay-[1000ms]' 
              : 'opacity-0 scale-95'
          }`}>
          {/* Icônes système par défaut */}
          {Object.entries(desktopIcons)
            .filter(([label]) => label !== 'LOGS_SYS') // Filtrer LOGS_SYS car c'est maintenant un widget
            .map(([label, position]) => {
            // Vérifier si c'est un raccourci de fichier
            const shortcutData = desktopShortcuts[label];
            if (shortcutData) {
              // Afficher le raccourci de fichier
              return (
                <DraggableDesktopIcon
                  key={label}
                  label={label}
                  icon={shortcutData.isImage && shortcutData.fileId ? (
                    // Aperçu image pour les fichiers image
                    <div className="w-8 h-8 border border-slate-500 bg-slate-800 flex items-center justify-center">
                      <img
                        src={getFilePreview(shortcutData.fileId)}
                        alt={label}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          // Fallback vers icône générique si l'image ne charge pas
                          e.currentTarget.style.display = 'none';
                          e.currentTarget.nextElementSibling?.classList.remove('hidden');
                        }}
                      />
                      <FileText size={iconSize === 'small' ? 16 : iconSize === 'large' ? 24 : 20} className="text-cyan-400 hidden" />
                    </div>
                  ) : (
                    // Icône générique pour les autres fichiers
                    <FileText size={iconSize === 'small' ? 24 : iconSize === 'large' ? 40 : 32} className="text-cyan-400" />
                  )}
                  onClick={() => {
                    if (shortcutData.type === 'file') {
                      handleOpenFile(shortcutData.fileId);
                    }
                  }}
                  unreadCount={0}
                  position={position}
                  onMove={(newX, newY) => moveIcon(label, newX, newY)}
                  size={iconSize}
                  isSelected={selectedIcon === label}
                  onSelect={setSelectedIcon}
                />
              );
            }

            // Icônes système par défaut
            let iconElement, onClick, unreadCount = 0;

            // Fonction pour obtenir le label traduit
            const getIconLabel = (key: string): string => {
              const labelMap: Record<string, string> = {
                'TABLEAU': t('desktop.board'),
                'FICHIERS': t('desktop.files'),
                'MESSAGER': t('desktop.messenger'),
                'L\'OREILLE': t('desktop.theEar'),
                'MA_STATION': t('desktop.myStation'),
                'CORBEILLE': t('desktop.trash')
              };
              return labelMap[key] || key;
            };

            switch (label) {
              case 'TABLEAU':
                iconElement = <AlbionIcons.Board size={iconSize === 'small' ? 24 : iconSize === 'large' ? 40 : 32} className="text-cyan-500" />;
                onClick = () => toggleWindow('board');
                break;
              case 'FICHIERS':
                iconElement = <AlbionIcons.Folder size={iconSize === 'small' ? 24 : iconSize === 'large' ? 40 : 32} className="text-blue-500" />;
                onClick = () => toggleWindow('explorer');
                break;
              case 'MESSAGER':
                iconElement = <AlbionIcons.Messenger size={iconSize === 'small' ? 24 : iconSize === 'large' ? 40 : 32} className="text-pink-500" />;
                onClick = () => toggleWindow('messenger');
                unreadCount = getUnreadCount();
                break;
              case 'L\'OREILLE':
                iconElement = <AlbionIcons.Radio size={iconSize === 'small' ? 24 : iconSize === 'large' ? 40 : 32} className="text-yellow-500" />;
                onClick = () => {
                  toggleWindow('theear');
                  window.dispatchEvent(new Event('EAR_OPENED'));
                };
                break;
              case 'MA_STATION':
                iconElement = <AlbionIcons.Computer size={iconSize === 'small' ? 24 : iconSize === 'large' ? 40 : 32} className="text-slate-400" />;
                onClick = () => toggleWindow('station-profile');
                break;
              case 'CORBEILLE':
                iconElement = <AlbionIcons.Trash size={iconSize === 'small' ? 24 : iconSize === 'large' ? 40 : 32} className="text-slate-500" />;
                onClick = () => { };
                break;
              default:
                return null;
            }

            return (
              <DraggableDesktopIcon
                key={label}
                label={getIconLabel(label)}
                icon={iconElement}
                onClick={onClick}
                unreadCount={unreadCount}
                position={position}
                onMove={(newX, newY) => moveIcon(label, newX, newY)}
                size={iconSize}
                isSelected={selectedIcon === label}
                onSelect={setSelectedIcon}
              />
            );
          })}
        </div>
        {/* FENÊTRES OUVERTES - Apparaissent après les icônes */}
        <div className={`transition-opacity duration-1000 ${
          desktopFadingOut 
            ? 'opacity-0 delay-[1000ms]' 
            : desktopInitialized 
              ? 'opacity-100 delay-[2000ms]' 
              : 'opacity-0'
          }`} style={{
            overflow: 'clip',
            position: 'relative',
            width: '100%',
            height: '100%',
            overflowAnchor: 'none'
          }}>
          
          {/* WIDGET HORLOGE */}
          <DesktopClock isVisible={widgetsVisible} />
          
          {/* WIDGET NOTES */}
          <DesktopNotes isVisible={widgetsVisible} />
          
          {/* WIDGET TERMINAL LOGS */}
          <TerminalLogsWidget 
            isVisible={widgetsVisible && isLogsWidgetVisible} 
            onClose={() => setIsLogsWidgetVisible(false)} 
          />
          
          {openWindows.map((win, index) => {
            const config = getWindowConfig(win);
            // Système de z-index basé sur la pile : position dans windowStack détermine le z-index
            const baseZIndex = 70; // Au-dessus des icônes (60)
            const stackIndex = windowStack.indexOf(win);
            const windowZIndex = activeWindow === win ? 100 : (stackIndex >= 0 ? baseZIndex + stackIndex : baseZIndex);
            const isMinimized = minimizedWindows.includes(win);

            let content = null;
            // Retirer le cas 'logs' car c'est maintenant un widget
            if (win === 'explorer') {
              content = (
                <Suspense fallback={<LoadingFallback componentName="Explorateur" />}>
                  <FileExplorer />
                </Suspense>
              );
            } else if (win === 'board') {
              content = (
                <Suspense fallback={<LoadingFallback componentName="Tableau" />}>
                  <BoardPanel key="board-persistent" />
                </Suspense>
              );
            } else if (win === 'enhancer' && enhancedImage) {
              content = (
                <Suspense fallback={<LoadingFallback componentName="Amélioration" />}>
                  <ImageEnhancer imageUrl={enhancedImage} />
                </Suspense>
              );
            } else if (win === 'messenger') {
              content = (
                <Suspense fallback={<LoadingFallback componentName="Messenger" />}>
                  <Messenger isVisible={true} />
                </Suspense>
              );
            } else if (win === 'theear') {
              content = (
                <Suspense fallback={<LoadingFallback componentName="L'Oreille" />}>
                  <TheEar />
                </Suspense>
              );
            } else if (win === 'station-profile') {
              content = (
                <Suspense fallback={<LoadingFallback componentName="Profil Station" />}>
                  <StationProfile />
                </Suspense>
              );
            } else if (win === 'wallpaper-selector') {
              content = (
                <Suspense fallback={<LoadingFallback componentName="Sélecteur" />}>
                  <WallpaperSelector onClose={() => closeWindow(win)} onWallpaperSelect={handleWallpaperChange} />
                </Suspense>
              );
            } else if (win === 'snake') {
              content = (
                <Suspense fallback={<LoadingFallback componentName="Snake" />}>
                  <Snake />
                </Suspense>
              );
            } else if (win === 'spaceinvaders') {
              content = (
                <Suspense fallback={<LoadingFallback componentName="Space Invaders" />}>
                  <SpaceInvaders />
                </Suspense>
              );
            } else if (win === 'albion-player') {
              content = (
                <Suspense fallback={<LoadingFallback componentName="Lecteur Audio" />}>
                  <AlbionPlayer />
                </Suspense>
              );
            } else if (win.startsWith('viewer_')) {
              const fileInstance = openFiles.find(f => f.id === win);
              if (fileInstance && FILE_CONTENTS[fileInstance.fileId]) {
                const fileData = { ...FILE_CONTENTS[fileInstance.fileId] };
                // Minotaur Glitch renaming
                if (isGlitching && (fileInstance.fileId === 'f_alice_photo_1' || fileInstance.fileId === 'f_alice_photo_2')) {
                  fileData.title = 'GÉMEAUX_ENTRY_01';
                }
                content = (
                  <Suspense fallback={<LoadingFallback componentName="Visionneuse" />}>
                    <FileViewer file={fileData} isActive={activeWindow === win} />
                  </Suspense>
                );
              }
            } else if (win.startsWith('properties_') && propertiesIcon) {
              content = (
                <Suspense fallback={<LoadingFallback componentName="Propriétés" />}>
                  <Properties iconName={propertiesIcon} onClose={() => closeWindow(win)} />
                </Suspense>
              );
            }

            if (!content) return null;

            return (
              <Window
                key={win}
                id={win}
                title={config.title}
                initialState={undefined} // Ne plus passer initialState pour éviter les conflits
                isActive={activeWindow === win}
                zIndex={windowZIndex}
                onClose={() => closeWindow(win)}
                onFocus={() => {
                  console.log(`Focus sur fenêtre ${win}`);
                  setActiveWindow(win);
                  setMinimizedWindows(prev => prev.filter(w => w !== win));
                  // Mettre à jour la pile : déplacer la fenêtre en haut
                  setWindowStack(prev => {
                    const newStack = prev.filter(w => w !== win);
                    return [...newStack, win];
                  });
                }}
                onMinimize={() => minimizeWindow(win)}
                onHelp={() => {
                  // Déclencher l'aide générique pour toutes les fenêtres
                  window.dispatchEvent(new CustomEvent('SHOW_WINDOW_HELP', { detail: { windowId: win } }));
                }}
                isMinimized={isMinimized}
                isAppearing={windowsAppearing.has(win)}
                resizable={win !== 'albion-player'} // Lecteur audio non-redimensionnable
              >
                {content}
              </Window>
            );
          })}

          {/* MESSENGER TOUJOURS MONTÉ (caché si pas ouvert) pour recevoir les messages */}
          {!openWindows.includes('messenger') && (
            <div style={{ display: 'none' }}>
              <Suspense fallback={null}>
                <Messenger isVisible={false} />
              </Suspense>
            </div>
          )}
        </div>
      </div>

      {/* BARRE DES TÂCHES - Apparaît avec le header et les fenêtres */}
      <div className={`fixed bottom-0 left-0 w-full h-10 bg-gradient-to-r from-slate-800 via-slate-700 to-slate-800 border-t border-slate-600 flex items-center px-4 gap-4 shadow-lg shadow-black/20 z-50 text-base uppercase font-bold text-slate-300 transition-all duration-1000 ${
        desktopFadingOut 
          ? 'translate-y-full opacity-0' 
          : desktopInitialized 
            ? 'translate-y-0 opacity-100 delay-[2000ms]' 
            : 'translate-y-full opacity-0'
        }`}>
        {/* Bouton Démarrer */}
        <button
          onClick={() => setStartMenu(!startMenu)}
          className={`px-4 py-1 border border-slate-500 font-bold text-sm transition-colors flex items-center gap-2 rounded-sm flex-shrink-0 ${startMenu
            ? 'bg-slate-600 text-cyan-400 border-cyan-500 shadow-inner'
            : 'bg-slate-700 text-slate-200 hover:bg-slate-600 hover:text-cyan-400 hover:border-cyan-500'
            }`}
        >
          <div className="w-3 h-3 bg-cyan-400 rounded-sm shadow-[0_0_4px_rgba(34,211,238,0.6)]"></div>
          ALBION OS
        </button>

        <div className="w-px h-6 bg-slate-600 flex-shrink-0"></div>

        {/* Zone des onglets avec scroll horizontal */}
        <div className="flex-1 overflow-hidden">
          <div className="flex gap-1 overflow-x-auto taskbar-scroll" style={{ scrollbarWidth: 'thin' }}>
            {openWindows.map(win => {
              const isMinimized = minimizedWindows.includes(win);
              const config = getWindowConfig(win);
              const unreadCount = win === 'messenger' ? getUnreadCount() : 0;

              return (
                <div
                  key={win}
                  className={`px-3 py-1 cursor-pointer hover:bg-slate-600 transition-colors border-l border-slate-600 whitespace-nowrap relative rounded-sm flex-shrink-0 min-w-0 max-w-[200px]
                    ${activeWindow === win ? 'text-slate-100 bg-slate-600 border-b-2 border-cyan-400' : ''}
                    ${isMinimized ? 'text-slate-500 italic' : ''}
                  `}
                  onClick={() => toggleWindow(win)}
                  onContextMenu={(e) => {
                    e.preventDefault();
                    e.stopPropagation(); // Empêcher la propagation vers le menu du bureau
                    const menuWidth = 120;
                    const menuHeight = 80;
                    const x = e.clientX + menuWidth > window.innerWidth ? e.clientX - menuWidth : e.clientX;
                    const y = e.clientY - menuHeight < 0 ? e.clientY : e.clientY - menuHeight;
                    setTaskbarMenu({ winId: win, x, y });
                  }}
                  title={config.title} // Tooltip pour voir le titre complet
                >
                  <div className="truncate">
                    {config.title}
                  </div>
                  {/* Badge de notification dans la barre des tâches */}
                  {unreadCount > 0 && (
                    <div className="absolute -top-1 -right-1 w-4 h-4 bg-red-600 text-white text-[8px] font-bold rounded-full flex items-center justify-center animate-pulse">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div className="flex-shrink-0"></div>
      </div>

      {/* CRT OVERLAY EFFECTS */}
      {gameSettings.crtEffects && (
        <>
          <div className={`fixed inset-0 z-[9990] pointer-events-none crt-scanlines opacity-50 ${isGlitching ? 'glitch-encrypted' : ''}`}></div>
          <div className="fixed inset-0 z-[9991] pointer-events-none crt-vignette"></div>
          <div className="fixed inset-0 z-[9992] pointer-events-none bg-gradient-radial from-transparent via-transparent to-black/20"></div>
        </>
      )}

      {/* SRS PORTAL TARGET */}
      <div id="srs-portal-root" className="fixed inset-0 pointer-events-none z-[10000]"></div>

      {/* NOTIFICATIONS CONTAINER */}
      <NotificationContainer
        fileNotifications={notifications}
        messageNotifications={messageNotifications}
        onDismissFile={dismissNotification}
        onDismissMessage={dismissMessageNotification}
        onOpenMessenger={() => toggleWindow('messenger')}
        isOpen={isNotificationPanelOpen}
        onClose={() => setIsNotificationPanelOpen(false)}
        onUnviewedCountChange={setUnviewedNotificationCount}
      />

      {/* PROCESS MONITOR */}
      <Suspense fallback={null}>
        <ProcessMonitor
          isOpen={isProcessMonitorOpen}
          onClose={() => setIsProcessMonitorOpen(false)}
          openWindows={openWindows}
          activeProcesses={notifications.length + messageNotifications.length}
          totalCpuLoad={cpuLoad}
          totalMemoryUsage={memoryUsage}
          stressLevel={stressLevel}
        />
      </Suspense>
      {/* START MENU */}
      {startMenu && (
        <div 
          className="fixed bottom-10 left-4 z-[1000] bg-slate-700 border border-slate-500 shadow-2xl shadow-black/40 w-64 rounded-sm overflow-hidden"
          style={{
            animation: 'menuDeploy 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)'
          }}
        >
          <style>{`
            @keyframes menuDeploy {
              0% {
                transform: translateY(10px) scaleY(0.8);
                opacity: 0;
                transform-origin: bottom;
              }
              100% {
                transform: translateY(0) scaleY(1);
                opacity: 1;
              }
            }
            @keyframes scanLine {
              0% {
                transform: translateY(-100%);
                opacity: 0.6;
              }
              100% {
                transform: translateY(300px);
                opacity: 0;
              }
            }
            @keyframes menuItemReveal {
              0% {
                opacity: 0;
                transform: translateX(-10px);
              }
              100% {
                opacity: 1;
                transform: translateX(0);
              }
            }
            @keyframes logoSpin {
              0% {
                transform: rotate(0deg) scale(0.8);
                opacity: 0;
              }
              100% {
                transform: rotate(180deg) scale(1);
                opacity: 1;
              }
            }
            @keyframes innerSquarePulse {
              0% {
                transform: scale(0.5);
                opacity: 0;
              }
              100% {
                transform: scale(1);
                opacity: 1;
              }
            }
            .menu-item {
              animation: menuItemReveal 0.2s ease-out forwards;
              opacity: 0;
            }
            .menu-item:nth-child(1) { animation-delay: 0.05s; }
            .menu-item:nth-child(2) { animation-delay: 0.08s; }
            .menu-item:nth-child(3) { animation-delay: 0.11s; }
            .menu-item:nth-child(4) { animation-delay: 0.14s; }
            .menu-item:nth-child(5) { animation-delay: 0.17s; }
            .menu-item:nth-child(6) { animation-delay: 0.20s; }
            .menu-item:nth-child(7) { animation-delay: 0.23s; }
            .menu-item:nth-child(8) { animation-delay: 0.26s; }
            .menu-item:nth-child(9) { animation-delay: 0.29s; }
            .menu-item:nth-child(10) { animation-delay: 0.32s; }
            .menu-item:nth-child(11) { animation-delay: 0.35s; }
            .scan-line {
              position: absolute;
              top: 0;
              left: 0;
              right: 0;
              height: 2px;
              background: linear-gradient(to bottom, transparent, rgba(34, 211, 238, 0.6), transparent);
              animation: scanLine 0.4s ease-out;
              pointer-events: none;
              z-index: 10;
            }
            .logo-animated {
              animation: logoSpin 0.8s ease-out;
            }
            .logo-inner {
              animation: innerSquarePulse 0.6s ease-out 0.2s;
            }
          `}</style>
          
          {/* Ligne de scan high-tech */}
          <div className="scan-line"></div>
          
          {/* Header du menu */}
          <div className="bg-gradient-to-r from-slate-800 to-slate-700 text-cyan-400 px-4 py-3 border-b border-slate-500 rounded-t-sm">
            <div className="flex items-center gap-3">
              <div className="w-6 h-6 bg-cyan-400 rounded-sm flex items-center justify-center shadow-[0_0_10px_rgba(34,211,238,0.6)] logo-animated">
                <div className="w-3 h-3 bg-slate-800 rounded-sm logo-inner"></div>
              </div>
              <div>
                <div className="text-sm font-bold">ALBION OS</div>
                <div className="text-[10px] text-slate-300">v1.0.93 - CELLULE MIROIR</div>
              </div>
            </div>
          </div>

          {/* Applications */}
          <div className="p-2">
            <div className="text-[10px] text-slate-500 uppercase tracking-wider px-2 py-1">{t('startMenu.applications')}</div>

            <button
              onClick={() => { toggleWindow('explorer'); setStartMenu(false); }}
              className="menu-item w-full text-left px-3 py-2 text-sm text-slate-300 hover:bg-slate-700 hover:text-cyan-400 transition-colors duration-150 flex items-center gap-3 relative overflow-hidden group"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/0 via-cyan-500/8 to-cyan-500/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-300"></div>
              <AlbionIcons.Folder size={16} className="text-blue-500 relative z-10" />
              <span className="relative z-10">{t('startMenu.fileExplorer')}</span>
            </button>

            <button
              onClick={() => { toggleWindow('board'); setStartMenu(false); }}
              className="menu-item w-full text-left px-3 py-2 text-sm text-slate-300 hover:bg-slate-700 hover:text-cyan-400 transition-colors duration-150 flex items-center gap-3 relative overflow-hidden group"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/0 via-cyan-500/8 to-cyan-500/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-300"></div>
              <AlbionIcons.Board size={16} className="text-cyan-500 relative z-10" />
              <span className="relative z-10">{t('startMenu.investigationBoard')}</span>
            </button>

            <button
              onClick={() => { toggleWindow('messenger'); setStartMenu(false); }}
              className="menu-item w-full text-left px-3 py-2 text-sm text-slate-300 hover:bg-slate-700 hover:text-cyan-400 transition-colors duration-150 flex items-center gap-3 relative overflow-hidden group"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/0 via-cyan-500/8 to-cyan-500/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-300"></div>
              <AlbionIcons.Messenger size={16} className="text-pink-500 relative z-10" />
              <span className="relative z-10">{t('startMenu.messenger')}</span>
            </button>

            <button
              onClick={() => { toggleWindow('theear'); setStartMenu(false); window.dispatchEvent(new Event('EAR_OPENED')); }}
              className="menu-item w-full text-left px-3 py-2 text-sm text-slate-300 hover:bg-slate-700 hover:text-cyan-400 transition-colors duration-150 flex items-center gap-3 relative overflow-hidden group"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/0 via-cyan-500/8 to-cyan-500/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-300"></div>
              <AlbionIcons.Radio size={16} className="text-yellow-500 relative z-10" />
              <span className="relative z-10">{t('startMenu.theEar')}</span>
            </button>

            <button
              onClick={() => { setIsLogsWidgetVisible(prev => !prev); setStartMenu(false); }}
              className="menu-item w-full text-left px-3 py-2 text-sm text-slate-300 hover:bg-slate-700 hover:text-cyan-400 transition-colors duration-150 flex items-center gap-3 relative overflow-hidden group"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/0 via-cyan-500/8 to-cyan-500/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-300"></div>
              <AlbionIcons.Terminal size={16} className="text-green-500 relative z-10" />
              <span className="relative z-10">{t('startMenu.systemLogs')}</span>
            </button>

            <button
              onClick={() => { toggleWindow('snake'); setStartMenu(false); }}
              className="menu-item w-full text-left px-3 py-2 text-sm bg-gradient-to-r from-pink-900/30 to-purple-900/30 hover:from-pink-800/40 hover:to-purple-800/40 border-l-2 border-pink-500 transition-all duration-150 flex items-center gap-3 group relative overflow-hidden"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-pink-500/0 via-pink-500/8 to-pink-500/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-300"></div>
              {/* Icône serpent pixelisé */}
              <div className="w-4 h-4 relative group-hover:scale-110 transition-transform duration-150 z-10">
                <div className="absolute top-0 left-0 w-1 h-1 bg-lime-400"></div>
                <div className="absolute top-0 left-1 w-1 h-1 bg-lime-400"></div>
                <div className="absolute top-1 left-2 w-1 h-1 bg-lime-400"></div>
                <div className="absolute top-2 left-2 w-1 h-1 bg-lime-400"></div>
                <div className="absolute top-3 left-2 w-1 h-1 bg-lime-400"></div>
                <div className="absolute top-3 left-1 w-1 h-1 bg-lime-400"></div>
                <div className="absolute top-3 left-0 w-1 h-1 bg-lime-400"></div>
                {/* Oeil du serpent */}
                <div className="absolute top-0 left-0 w-0.5 h-0.5 bg-red-500"></div>
              </div>
              <span className="text-pink-300 group-hover:text-pink-200 font-medium relative z-10">
                {t('snake.title')}
              </span>
              <span className="ml-auto text-[10px] text-pink-500/60 italic relative z-10">non-officiel</span>
            </button>

            <button
              onClick={() => { toggleWindow('spaceinvaders'); setStartMenu(false); }}
              className="menu-item w-full text-left px-3 py-2 text-sm bg-gradient-to-r from-cyan-900/30 to-blue-900/30 hover:from-cyan-800/40 hover:to-blue-800/40 border-l-2 border-cyan-500 transition-all duration-150 flex items-center gap-3 group relative overflow-hidden"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/0 via-cyan-500/8 to-cyan-500/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-300"></div>
              {/* Icône alien pixelisé */}
              <div className="w-4 h-4 relative group-hover:scale-110 transition-transform duration-150 z-10">
                <div className="absolute top-0 left-1 w-2 h-1 bg-cyan-400"></div>
                <div className="absolute top-1 left-0 w-4 h-1 bg-cyan-400"></div>
                <div className="absolute top-2 left-0 w-4 h-1 bg-cyan-400"></div>
                <div className="absolute top-3 left-0 w-1 h-1 bg-cyan-400"></div>
                <div className="absolute top-3 right-0 w-1 h-1 bg-cyan-400"></div>
                {/* Yeux */}
                <div className="absolute top-1 left-1 w-0.5 h-0.5 bg-red-500"></div>
                <div className="absolute top-1 right-1 w-0.5 h-0.5 bg-red-500"></div>
              </div>
              <span className="text-cyan-300 group-hover:text-cyan-200 font-medium relative z-10">
                {t('spaceInvaders.title')}
              </span>
              <span className="ml-auto text-[10px] text-cyan-500/60 italic relative z-10">non-officiel</span>
            </button>

            <button
              onClick={() => { toggleWindow('albion-player'); setStartMenu(false); }}
              className="menu-item w-full text-left px-3 py-2 text-sm bg-gradient-to-r from-green-900/30 to-blue-900/30 hover:from-green-800/40 hover:to-blue-800/40 border-l-2 border-green-500 transition-all duration-150 flex items-center gap-3 group relative overflow-hidden"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-green-500/0 via-green-500/8 to-green-500/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-300"></div>
              {/* Icône lecteur audio pixelisé */}
              <div className="w-4 h-4 relative group-hover:scale-110 transition-transform duration-150 z-10">
                <div className="absolute top-0 left-0 w-4 h-3 border border-green-400 bg-green-900/50"></div>
                <div className="absolute top-1 left-1 w-2 h-1 bg-green-400"></div>
                <div className="absolute bottom-0 left-0 w-1 h-1 bg-green-400"></div>
                <div className="absolute bottom-0 left-1.5 w-1 h-1 bg-green-400"></div>
                <div className="absolute bottom-0 right-0 w-1 h-1 bg-green-400"></div>
              </div>
              <span className="text-green-300 group-hover:text-green-200 font-medium relative z-10">
                WavePlayer
              </span>
              <span className="ml-auto text-[10px] text-green-500/60 italic relative z-10">non-officiel</span>
            </button>
          </div>

          <div className="h-px bg-slate-600 mx-2"></div>

          {/* Outils système */}
          <div className="p-2">
            <div className="text-[10px] text-slate-500 uppercase tracking-wider px-2 py-1">{t('startMenu.system')}</div>

            <button
              onClick={() => { setIsSettingsOpen(true); setStartMenu(false); }}
              className="menu-item w-full text-left px-3 py-2 text-sm text-slate-300 hover:bg-slate-700 hover:text-cyan-400 transition-colors duration-150 flex items-center gap-3 relative overflow-hidden group"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/0 via-cyan-500/8 to-cyan-500/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-300"></div>
              <div className="w-4 h-4 border border-slate-500 rounded flex items-center justify-center relative z-10">
                <div className="w-2 h-2 bg-slate-500 rounded"></div>
              </div>
              <span className="relative z-10">{t('startMenu.settings')}</span>
            </button>
          </div>

          <div className="h-px bg-slate-600 mx-2"></div>

          {/* Arrêt */}
          <div className="p-2">
            <button
              onClick={startRestartSequence}
              className="w-full text-left px-3 py-2 text-sm text-slate-300 hover:bg-slate-700 hover:text-cyan-400 transition-colors flex items-center gap-3"
            >
              <div className="w-4 h-4 border border-red-500 rounded flex items-center justify-center">
                <div className="w-2 h-1 bg-red-500"></div>
              </div>
              {t('startMenu.restart')}
            </button>

            <button
              onClick={() => { handleQuitRequest(); }}
              className="w-full text-left px-3 py-2 text-sm text-slate-300 hover:bg-slate-700 hover:text-cyan-400 transition-colors flex items-center gap-3 mt-1"
            >
              <div className="w-4 h-4 border border-red-500 rounded flex items-center justify-center">
                <div className="w-2 h-2 bg-red-500 rounded-full"></div>
              </div>
              {t('startMenu.shutdown')}
            </button>
          </div>
        </div>
      )}

      {/* DESKTOP CONTEXT MENU */}
      {desktopContextMenu && (
        <div
          className="fixed z-[1000] bg-slate-800 border border-slate-600 shadow-2xl shadow-black/50 p-1 min-w-[200px]"
          style={{ left: desktopContextMenu.x, top: desktopContextMenu.y }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            className="w-full text-left px-3 py-2 text-sm text-slate-300 hover:bg-slate-700 hover:text-cyan-400 font-bold transition-colors flex items-center gap-2"
            onClick={handleAutoArrangeIcons}
          >
            <AlbionIcons.Board size={16} className="text-cyan-500" />
            {t('contextMenu.autoArrangeIcons')}
          </button>

          <div className="h-[1px] bg-slate-600 my-1" />

          <div className="px-3 py-1 text-sm text-slate-400 font-bold uppercase tracking-wider">{t('contextMenu.iconSize.title')}</div>
          <button
            className={`w-full text-left px-6 py-1.5 text-sm transition-colors ${iconSize === 'small' ? 'bg-slate-700 text-cyan-400' : 'text-slate-300 hover:bg-slate-700 hover:text-cyan-400'}`}
            onClick={() => handleIconSizeChange('small')}
          >
            {t('contextMenu.iconSize.small')}
          </button>
          <button
            className={`w-full text-left px-6 py-1.5 text-sm transition-colors ${iconSize === 'medium' ? 'bg-slate-700 text-cyan-400' : 'text-slate-300 hover:bg-slate-700 hover:text-cyan-400'}`}
            onClick={() => handleIconSizeChange('medium')}
          >
            {t('contextMenu.iconSize.medium')}
          </button>
          <button
            className={`w-full text-left px-6 py-1.5 text-sm transition-colors ${iconSize === 'large' ? 'bg-slate-700 text-cyan-400' : 'text-slate-300 hover:bg-slate-700 hover:text-cyan-400'}`}
            onClick={() => handleIconSizeChange('large')}
          >
            {t('contextMenu.iconSize.large')}
          </button>

          <div className="h-[1px] bg-slate-600 my-1" />

          <button
            className="w-full text-left px-3 py-2 text-sm text-slate-300 hover:bg-slate-700 hover:text-cyan-400 font-medium transition-colors"
            onClick={handleOpenWallpaperSelector}
          >
            {t('contextMenu.customizeWallpaper')}
          </button>

          <button
            className="w-full text-left px-3 py-2 text-sm text-slate-300 hover:bg-slate-700 hover:text-cyan-400 font-medium transition-colors"
            onClick={handleResetWallpaper}
          >
            {t('contextMenu.defaultWallpaper')}
          </button>
        </div>
      )}

      {/* ICON CONTEXT MENU */}
      {iconContextMenu && (
        <div
          className="fixed z-[1000] bg-slate-800 border border-slate-600 shadow-2xl shadow-black/50 p-1 min-w-[160px]"
          style={{ left: iconContextMenu.x, top: iconContextMenu.y }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            className="w-full text-left px-3 py-2 text-sm text-slate-300 hover:bg-slate-700 hover:text-cyan-400 font-bold transition-colors flex items-center gap-2"
            onClick={() => handleOpenIcon(iconContextMenu.iconName)}
          >
            {t('contextMenu.open')}
          </button>

          <div className="h-[1px] bg-slate-600 my-1" />

          <button
            className="w-full text-left px-3 py-2 text-sm text-slate-300 hover:bg-slate-700 hover:text-cyan-400 font-medium transition-colors"
            onClick={() => handleDeleteIcon(iconContextMenu.iconName)}
          >
            {t('contextMenu.deleteFromDesktop')}
          </button>

          <button
            className="w-full text-left px-3 py-2 text-sm text-slate-300 hover:bg-slate-700 hover:text-cyan-400 font-medium transition-colors"
            onClick={() => {
              // Copier le nom dans le presse-papier (simulation)
              navigator.clipboard?.writeText(iconContextMenu.iconName);
              setIconContextMenu(null);
            }}
          >
            {t('contextMenu.copy')}
          </button>

          <div className="h-[1px] bg-slate-600 my-1" />

          <button
            className="w-full text-left px-3 py-2 text-sm text-slate-300 hover:bg-slate-700 hover:text-cyan-400 font-medium transition-colors"
            onClick={() => handleIconProperties(iconContextMenu.iconName)}
          >
            {t('contextMenu.properties')}
          </button>
        </div>
      )}

      {/* TASKBAR CONTEXT MENU */}
      {taskbarMenu && (
        <div
          className="fixed z-[1000] bg-slate-800 border border-slate-600 shadow-2xl shadow-black/50 p-1 min-w-[160px]"
          style={{ left: taskbarMenu.x, top: taskbarMenu.y }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            className="w-full text-left px-3 py-2 text-sm text-slate-300 hover:bg-slate-700 hover:text-cyan-400 font-bold transition-colors flex items-center gap-2"
            onClick={() => {
              closeWindow(taskbarMenu.winId);
              setTaskbarMenu(null);
            }}
          >
            <AlbionIcons.Trash size={16} className="text-red-500" />
            Terminer Processus
          </button>
          <div className="h-[1px] bg-slate-600 my-1" />
          <button
            className="w-full text-left px-3 py-2 text-sm text-slate-300 hover:bg-slate-700 hover:text-cyan-400 font-medium transition-colors"
            onClick={() => setTaskbarMenu(null)}
          >
            Annuler
          </button>
        </div>
      )}

      {/* FENÊTRE D'AIDE GÉNÉRIQUE */}
      {helpWindow && (
        <HelpWindow
          windowId={helpWindow.windowId}
          title={helpWindow.title}
          onClose={() => setHelpWindow(null)}
        />
      )}

      {/* GLOBAL CLICK HANDLER TO CLOSE MENUS */}
      
      {/* Styles CSS pour les animations de transition wallpaper */}
      <style>{`
        @keyframes fade-in {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        
        .animate-fade-in {
          animation: fade-in 0.5s ease-in-out;
        }
      `}</style>
      <div
        className={`fixed inset-0 z-[900] pointer-events-none ${taskbarMenu || desktopContextMenu || startMenu || iconContextMenu || helpWindow ? 'pointer-events-auto' : ''}`}
        onClick={() => {
          setTaskbarMenu(null);
          setDesktopContextMenu(null);
          setStartMenu(false);
          setIconContextMenu(null);
          setHelpWindow(null);
        }}
      />

      {/* GAME SETTINGS MODAL */}
      <Suspense fallback={null}>
        <GameSettings
          isOpen={isSettingsOpen}
          onClose={() => setIsSettingsOpen(false)}
        />
      </Suspense>

      {/* SAVE DIALOG */}
      <SaveDialog
        isOpen={isSaveDialogOpen}
        onSave={handleSaveAndQuit}
        onDontSave={handleQuitWithoutSaving}
        onCancel={handleCancelQuit}
        onSaveAndVisitCommunity={handleSaveAndVisitCommunity}
        onSaveAndVisitItch={handleSaveAndVisitItch}
      />

      {/* OVERLAY DE SHUTDOWN - Bloque les interactions pendant la séquence */}
      {isShuttingDownSequence && (
        <div className="fixed inset-0 z-[9998] bg-transparent pointer-events-auto" />
      )}
    </div>
  );
};

export default UchroniaOS;